import React, { useState, useMemo, memo, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { StatusBadge, TypeBadge, getRowStatusClass } from './StatusBadge';
import { ActionRequiseEmailCell } from './ActionRequiseEmailCell';
import { 
  ChevronUp, 
  ChevronDown, 
  Search, 
  MoreHorizontal,
  Link2Off,
  Trash2,
  Edit,
  Check,
  X,
  CheckSquare,
  Square,
  Tag,
  CheckCircle,
  AlertTriangle,
  Filter,
  MapPin,
  Eye,
  EyeOff,
  Pencil,
  Link as LinkIcon,
  Copy,
  ArrowLeft,
  ArrowRight,
  GripHorizontal,
  ChevronRight,
  Users,
  Phone,
  Mail,
  Link2,
  Radio,
  Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { findStadium } from '../../data/stadiums';
import StadiumPlanModal from './StadiumPlanModal';

// Sort icon component - extracted to avoid nested component issue
const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey) return null;
  return sortConfig.direction === 'asc' 
    ? <ChevronUp className="h-3 w-3 ml-1" />
    : <ChevronDown className="h-3 w-3 ml-1" />;
};

// LITIGE menu item — apparaît dans le dropdown statut, uniquement sur les VENTE dans la vue VENTE ou LITIGE
// 🔒 Pour retirer la feature LITIGE : supprimer ce composant + ses appels + le bouton + onToggleLitige
const LitigeMenuItem = ({ ticket, viewType, onToggleLitige }) => {
  if (!onToggleLitige) return null;
  if (ticket.type !== 'VENTE') return null;
  if (viewType !== 'vente' && viewType !== 'litige' && !ticket.litige) return null;
  const isOn = !!ticket.litige;
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={(e) => { e.stopPropagation?.(); onToggleLitige(ticket, !isOn); }}
        className={isOn ? 'text-purple-300' : 'text-purple-400'}
        data-testid={`menu-litige-${ticket.id}`}
      >
        <AlertTriangle className="h-4 w-4 mr-2" />
        {isOn ? 'Retirer du LITIGE' : 'Mettre en LITIGE'}
      </DropdownMenuItem>
    </>
  );
};

// ACTION REQUISE menu item — yellow, same behavior as LITIGE
const ActionRequiseMenuItem = ({ ticket, viewType, onToggleActionRequise }) => {
  if (!onToggleActionRequise) return null;
  if (ticket.type !== 'VENTE') return null;
  if (viewType !== 'vente' && viewType !== 'litige' && viewType !== 'action-requise' && !ticket.action_requise) return null;
  const isOn = !!ticket.action_requise;
  return (
    <DropdownMenuItem
      onClick={(e) => { e.stopPropagation?.(); onToggleActionRequise(ticket, !isOn); }}
      className={isOn
        ? 'text-white bg-emerald-600 hover:bg-emerald-700 focus:bg-emerald-700 font-semibold'
        : 'text-yellow-400'}
      data-testid={`menu-action-requise-${ticket.id}`}
    >
      {isOn ? <CheckCircle className="h-4 w-4 mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
      {isOn ? '✅ TRAITER (sortir d\'ACTION REQUISE)' : 'Marquer ACTION REQUISE'}
    </DropdownMenuItem>
  );
};


// Completely isolated column filter component to prevent re-render loops
// Uses local state for selections and only commits to parent on close
// NOW: Uses pre-loaded filter values from backend for complete list
const ColumnFilterDropdown = memo(function ColumnFilterDropdown({
  columnKey,
  tickets,
  columnFilters,
  otherFiltersTickets, // Tickets filtered by OTHER columns (not this one)
  allFilterValues, // NEW: Pre-loaded values from backend
  onFilterChange
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  // Local state to track selections without causing parent re-renders
  const [localSelected, setLocalSelected] = useState([]);
  
  // Special constant for empty values
  const EMPTY_VALUE_MARKER = '__VIDE__';
  
  // Helper: format date_attribution ISO → DD/MM/YYYY for filter display
  const isoToDateOnly = (iso) => {
    if (!iso) return iso;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso).substring(0, 10);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    } catch {
      return String(iso).substring(0, 10);
    }
  };

  // Use backend values if available, otherwise fall back to tickets
  const uniqueValues = useMemo(() => {
    let values = [];
    let hasEmptyValues = false;
    
    // PRIORITY 1: Use pre-loaded backend values (complete list)
    if (allFilterValues && allFilterValues[columnKey] && allFilterValues[columnKey].length > 0) {
      values = [...allFilterValues[columnKey]];
    } else {
      // FALLBACK: Use otherFiltersTickets if available, otherwise all tickets
      const sourceTickets = otherFiltersTickets || tickets;
      const valuesSet = new Set();
      sourceTickets.forEach(t => {
        // For date_achat, also check date_operation (same field, different key)
        let val = t[columnKey];
        if (columnKey === 'date_achat' && !val) val = t.date_operation;
        // For date_attribution, aggregate to date only (DD/MM/YYYY)
        if (columnKey === 'date_attribution' && val) val = isoToDateOnly(val);
        if (val !== undefined && val !== null && val !== '') {
          valuesSet.add(String(val).substring(0, 100));
        } else {
          hasEmptyValues = true;
        }
      });
      values = Array.from(valuesSet).sort((a, b) => {
        // For date_attribution, sort newest first
        if (columnKey === 'date_attribution') {
          const parseFR = (s) => {
            const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            return m ? new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1])).getTime() : 0;
          };
          return parseFR(b) - parseFR(a);
        }
        return String(a).localeCompare(String(b));
      });
    }
    
    // Check if there are empty values in the dataset
    const sourceTickets = otherFiltersTickets || tickets;
    hasEmptyValues = sourceTickets.some(t => {
      let val = t[columnKey];
      if (columnKey === 'date_achat' && !val) val = t.date_operation;
      return val === undefined || val === null || val === '';
    });
    
    // Add "(Vide)" option at the beginning if there are empty values
    if (hasEmptyValues) {
      return [EMPTY_VALUE_MARKER, ...values];
    }
    
    return values;
  }, [allFilterValues, otherFiltersTickets, tickets, columnKey]);
  
  // Filter values based on search
  const filteredValues = useMemo(() => {
    if (!searchValue) return uniqueValues;
    const term = searchValue.toLowerCase();
    return uniqueValues.filter(v => String(v).toLowerCase().includes(term));
  }, [uniqueValues, searchValue]);
  
  // Sync local state with parent when dropdown opens
  const handleOpenChange = (open) => {
    if (open) {
      // When opening, sync local state with parent state
      setLocalSelected(columnFilters[columnKey] || []);
      setSearchValue('');
    } else {
      // When closing, commit local changes to parent
      const hasChanges = JSON.stringify(localSelected.sort()) !== JSON.stringify((columnFilters[columnKey] || []).sort());
      if (hasChanges) {
        onFilterChange(columnKey, localSelected.length > 0 ? localSelected : undefined);
      }
    }
    setIsOpen(open);
  };
  
  // Toggle value in local state (no parent update)
  const toggleLocalValue = (value) => {
    setLocalSelected(prev => {
      const isSelected = prev.includes(value);
      if (isSelected) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };
  
  // Clear filter (commit immediately)
  const handleClear = () => {
    setLocalSelected([]);
    onFilterChange(columnKey, undefined);
    setIsOpen(false);
  };
  
  const selectedCount = columnFilters[columnKey]?.length || 0;
  
  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className={cn(
          "p-0.5 rounded hover:bg-zinc-700 transition-colors flex items-center gap-0.5",
          selectedCount > 0 && "text-[#C4A052]"
        )}>
          <Filter className="h-3 w-3" />
          {selectedCount > 0 && (
            <span className="text-[10px] bg-[#C4A052] text-black rounded-full px-1 font-bold">
              {selectedCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-64 p-0" 
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={() => handleOpenChange(false)}
      >
        {/* Search input */}
        <div className="sticky top-0 bg-zinc-800 z-10 p-2 border-b border-zinc-700">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full h-7 pl-7 pr-2 text-xs bg-zinc-700 border border-zinc-600 rounded text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#C4A052]"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>
        </div>
        
        {/* Clear filter option */}
        <DropdownMenuItem 
          onSelect={(e) => {
            e.preventDefault();
            handleClear();
          }}
          className={cn(localSelected.length === 0 && "bg-zinc-700")}
        >
          <X className="h-3 w-3 mr-2" />
          <span className="text-zinc-300">Effacer les filtres</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        
        {/* Values list */}
        <div className="max-h-64 overflow-y-auto">
          {filteredValues.map((value, idx) => {
            const isChecked = localSelected.includes(value);
            const displayValue = value === EMPTY_VALUE_MARKER ? '(Vide)' : (value || '(vide)');
            return (
              <DropdownMenuCheckboxItem 
                key={`${columnKey}-${idx}-${value}`}
                checked={isChecked}
                onCheckedChange={() => toggleLocalValue(value)}
                onSelect={(e) => e.preventDefault()}
                className={cn("text-xs cursor-pointer", value === EMPTY_VALUE_MARKER && "text-zinc-400 italic")}
              >
                <span className="truncate max-w-48">{displayValue}</span>
              </DropdownMenuCheckboxItem>
            );
          })}
          {filteredValues.length === 0 && (
            <div className="p-2 text-xs text-zinc-500 text-center">Aucun résultat</div>
          )}
        </div>
        
        {/* Apply button */}
        {localSelected.length > 0 && (
          <div className="border-t border-zinc-700 p-2">
            <button
              onClick={() => handleOpenChange(false)}
              className="w-full h-7 text-xs bg-[#C4A052] text-black rounded font-semibold hover:bg-[#D4B062] transition-colors"
            >
              Appliquer ({localSelected.length} sélectionné{localSelected.length > 1 ? 's' : ''})
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// Format event name: Normalize match number display
// "M76 -( ROUND OF 32 )" → "M76 - Round of 32"
// "1C vs 2F - World Cup - Round of M32 (Match M76)" → "M76 - 1C vs 2F - Round of 32"
const formatEventName = (eventName) => {
  if (!eventName) return '-';
  
  // Extract match number
  const matchNumMatch = eventName.match(/\bM?(\d{1,3})\b/i);
  const matchNum = matchNumMatch ? parseInt(matchNumMatch[1], 10) : null;
  
  // Clean up and normalize the name
  let cleaned = eventName
    .replace(/\s+/g, ' ')  // Multiple spaces to single
    .replace(/\(\s*Match\s*M?\d+\s*\)/gi, '')  // Remove "(Match M76)"
    .replace(/\bM0+(\d)/gi, 'M$1')  // M002 → M2 (M obligatoire pour éviter "16:00" → "16:M0")
    .replace(/\.\s*WC\s*$/i, ' - World Cup')  // .WC → - World Cup
    .replace(/-\s*\(\s*/g, ' - ')  // "-( " → " - "
    .replace(/\s*\)\s*/g, ' ')  // " ) " → " "
    .trim();
  
  // If match number found, ensure it's at the start for consistency
  if (matchNum && !cleaned.match(/^M\d/i)) {
    // Remove match number from wherever it is
    cleaned = cleaned.replace(/\s*[-·]\s*M\d+\s*/gi, ' ').trim();
    cleaned = `M${matchNum} - ${cleaned}`;
  }
  
  return cleaned;
};

// Extract match number for sorting  
const extractMatchNum = (eventName) => {
  if (!eventName) return 9999;
  const match = eventName.match(/\bM?(\d{1,3})\b/i);
  return match ? parseInt(match[1], 10) : 9999;
};

// Colonnes pour ACHAT - BASE
const ACHAT_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'date_achat', label: 'DATE ACHAT', width: '95px' },
  { key: 'evenement', label: 'MATCH', width: '220px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'block', label: 'BLOCK', width: '70px' },
  { key: 'rang', label: 'RANG', width: '60px' },
  { key: 'place', label: 'SIEGE', width: '60px' },
  { key: 'qty', label: 'QTY', width: '50px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'lieu', label: 'COURS', width: '100px' },
  { key: 'email_compte', label: 'E-MAIL DU COMPTE', width: '180px' },
  { key: 'pw_compte', label: 'PW DU COMPTE', width: '120px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '130px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Colonnes ROLAND GARROS - ACHAT (ordre fixe)
const RG_ACHAT_COLUMNS = [
  { key: 'select', label: '', width: '40px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'evenement', label: 'MATCH', width: '200px' },
  { key: 'session', label: 'SESSION', width: '80px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'tribune', label: 'TRIBUNE', width: '100px' },
  { key: 'acces', label: 'ACCÈS', width: '80px' },
  { key: 'escalier', label: 'ESCALIER', width: '80px' },
  { key: 'rang', label: 'ROW', width: '60px' },
  { key: 'place', label: 'SEAT', width: '60px' },
  { key: 'lieu', label: 'COURS', width: '100px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'nom_client', label: 'NAMES', width: '130px' },
  { key: 'dob_client', label: 'DOB', width: '90px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'email_compte', label: 'MAIL DU COMPTE', width: '180px' },
  { key: 'pw_compte', label: 'PW DU COMPTE', width: '120px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'date_achat', label: 'DATE ACHAT', width: '95px' },
  { key: 'ref_vente', label: 'REF VENTE', width: '110px' },
  { key: 'acheteur_original', label: 'ACHETEUR ORIGINAL', width: '130px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];
const RG_VENTE_COLUMNS = [
  { key: 'select', label: '', width: '40px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'evenement', label: 'MATCH', width: '200px' },
  { key: 'session', label: 'SESSION', width: '80px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'qty', label: 'QTY', width: '60px' },
  { key: 'tribune', label: 'TRIBUNE', width: '100px' },
  { key: 'acces', label: 'ACCÈS', width: '80px' },
  { key: 'escalier', label: 'ESCALIER', width: '80px' },
  { key: 'rang', label: 'ROW', width: '60px' },
  { key: 'place', label: 'SEAT', width: '60px' },
  { key: 'lieu', label: 'COURS', width: '100px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'nom_client', label: 'NAMES', width: '130px' },
  { key: 'dob_client', label: 'DOB', width: '90px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'source', label: 'PLATEFORME', width: '100px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'date_operation', label: 'VENDU LE', width: '90px' },
  { key: 'acheteur_original', label: 'ACHETEUR ORIGINAL', width: '130px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Colonnes ROLAND GARROS - TOUT (ordre fixe)
const RG_TOUT_COLUMNS = [
  { key: 'select', label: '', width: '40px' },
  { key: 'type', label: 'TYPE', width: '70px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'evenement', label: 'MATCH', width: '200px' },
  { key: 'session', label: 'SESSION', width: '80px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'qty', label: 'QTY', width: '60px' },
  { key: 'tribune', label: 'TRIBUNE', width: '100px' },
  { key: 'acces', label: 'ACCÈS', width: '80px' },
  { key: 'escalier', label: 'ESCALIER', width: '80px' },
  { key: 'rang', label: 'ROW', width: '60px' },
  { key: 'place', label: 'SEAT', width: '60px' },
  { key: 'lieu', label: 'COURS', width: '100px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'nom_client', label: 'NAMES', width: '130px' },
  { key: 'dob_client', label: 'DOB', width: '90px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'email_compte', label: 'MAIL DU COMPTE', width: '180px' },
  { key: 'pw_compte', label: 'PW DU COMPTE', width: '120px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'date_achat', label: 'DATE ACHAT', width: '95px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'acheteur_original', label: 'ACHETEUR ORIGINAL', width: '130px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Fonction pour obtenir les colonnes ACHAT selon le projet
const getAchatColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
  const isWC = project && project.toUpperCase().startsWith('WORLD CUP');
  if (isRG) return RG_ACHAT_COLUMNS;
  if (isWC) {
    return ACHAT_COLUMNS_BASE.filter(col => col.key !== 'ref_achat').map(col => {
      if (col.key === 'lieu') return { ...col, label: 'LIEU' };
      return col;
    });
  }
  return ACHAT_COLUMNS_BASE;
};

// Colonnes pour VENTE - BASE (sans colonnes RG)
const VENTE_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'date_operation', label: 'VENDU LE', width: '90px' },
  { key: 'source', label: 'PLATEFORME', width: '100px' },
  { key: 'evenement', label: 'MATCH', width: '220px' },
  { key: 'lieu', label: 'LIEU', width: '150px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ville', label: 'VILLE', width: '100px' },
  { key: 'pays', label: 'PAYS', width: '80px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'block', label: 'BLOCK', width: '70px' },
  { key: 'rang', label: 'RANG', width: '60px' },
  { key: 'qty', label: 'QTY', width: '50px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'note', label: 'NOTE', width: '100px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '130px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '180px' },
  { key: 'linked_mail_du_compte', label: 'MAIL COMPTE (ACHAT)', width: '180px' },
  { key: 'linked_pw_du_compte', label: 'PW COMPTE (ACHAT)', width: '120px' },
  { key: 'linked_source_achat', label: 'SOURCE ACHAT', width: '100px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Fonction pour obtenir les colonnes VENTE selon le projet
const getVenteColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
  if (isRG) return RG_VENTE_COLUMNS;
  return VENTE_COLUMNS_BASE;
};

// Colonnes pour TOUT - BASE (sans colonnes RG)
const TOUT_COLUMNS_BASE = [
  { key: 'select', label: '', width: '40px' },
  { key: 'type', label: 'TYPE', width: '70px' },
  { key: 'ref_achat', label: 'REF ACHAT', width: '110px' },
  { key: 'date_achat', label: 'DATE ACHAT', width: '95px' },
  { key: 'evenement', label: 'MATCH', width: '200px' },
  { key: 'categorie', label: 'CATÉGORIE', width: '100px' },
  { key: 'block', label: 'BLOCK', width: '70px' },
  { key: 'rang', label: 'RANG', width: '60px' },
  { key: 'place', label: 'SIEGE', width: '60px' },
  { key: 'date_evenement', label: 'DATE', width: '90px' },
  { key: 'qty', label: 'QTY', width: '50px' },
  { key: 'prix', label: 'PRIX', width: '90px' },
  { key: 'source', label: 'SOURCE', width: '100px' },
  { key: 'ref_vente', label: 'REF DE VENTE', width: '110px' },
  { key: 'date_operation', label: 'VENDU LE', width: '90px' },
  { key: 'lieu', label: 'LIEU', width: '130px' },
  { key: 'plan', label: 'PLAN', width: '70px' },
  { key: 'ville', label: 'VILLE', width: '90px' },
  { key: 'pays', label: 'PAYS', width: '70px' },
  { key: 'note', label: 'NOTE', width: '80px' },
  { key: 'email_compte', label: 'E-MAIL COMPTE', width: '160px' },
  { key: 'pw_compte', label: 'PW COMPTE', width: '100px' },
  { key: 'nom_client', label: 'NOM CLIENT', width: '120px' },
  { key: 'mail_client', label: 'MAIL CLIENT', width: '160px' },
  { key: 'date_attribution', label: 'DATE ATTRIB.', width: '105px' },
  { key: 'attribuer', label: 'ATTRIBUER', width: '100px' },
  { key: 'actions', label: '', width: '50px' },
];

// Fonction pour obtenir les colonnes TOUT selon le projet
const getToutColumns = (project) => {
  const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
  const isWC = project && project.toUpperCase().startsWith('WORLD CUP');
  if (isRG) return RG_TOUT_COLUMNS;
  if (isWC) {
    return TOUT_COLUMNS_BASE.filter(col => col.key !== 'ref_achat');
  }
  return TOUT_COLUMNS_BASE;
};

// Composant de récapitulatif par match quand un filtre est actif
const FilterSummaryPanel = ({ tickets, hasFilter, searchTerm, onMatchClick, selectedMatchFilter, onClearMatchFilter, project = '', viewType = 'tout' }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('yas_recap_collapsed') !== '0');
  
  // Calculer les stats par match - TOUJOURS appelé (règle des hooks)
  const matchStats = useMemo(() => {
    if (!hasFilter || tickets.length === 0) {
      return { matches: [], grandTotalQty: 0, grandTotalPrice: 0, grandTotalAttribue: 0, grandTotalNonAttribue: 0 };
    }
    
    // En vue VENTE : on agrège les VENTE (pour voir le récap des billets vendus par match).
    // En vue ACHAT / TOUT : on agrège les ACHAT (les VENTE sont la contrepartie, pas du stock en plus).
    const aggregateType = viewType === 'vente' ? 'VENTE' : 'ACHAT';
    const activeTickets = tickets.filter(t => 
      t.statut !== 'ANNULEE' && t.statut !== 'ANNULÉ' && t.type === aggregateType
    );
    
    // Récupérer les prix de vente pour calculer le total financier (uniquement utile en mode ACHAT)
    const venteTickets = tickets.filter(t => 
      t.type === 'VENTE' && t.statut !== 'ANNULEE' && t.statut !== 'ANNULÉ'
    );
    // Map ref_vente → prix de vente
    const ventePriceMap = {};
    venteTickets.forEach(v => {
      const ref = v.ref_vente;
      if (ref && ref !== '' && ref !== '-' && ref !== 'N/A') {
        ventePriceMap[ref] = (ventePriceMap[ref] || 0) + (parseFloat(v.prix) || 0);
      }
    });
    
    const stats = {};
    let grandTotalQty = 0;
    let grandTotalPrice = 0;
    let grandTotalAttribue = 0;
    let grandTotalNonAttribue = 0;
    
    activeTickets.forEach(ticket => {
      // Extraire le numéro de match
      const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
      const matchNum = matchNumMatch ? `M${matchNumMatch[1]}` : 'Autre';
      
      // Pour Roland Garros: séparer aussi par court + session
      const isRG = (project || '').toUpperCase().includes('ROLAN');
      let courtKey = '';
      let sessionKey = '';
      let courtLabel = '';
      let sessionLabel = '';
      if (isRG) {
        const lieuStr = (ticket.lieu || '').toLowerCase();
        const evStr = (ticket.evenement || '').toLowerCase();
        const sessStr = (ticket.session || '').toLowerCase();
        if (lieuStr.includes('lenglen') || evStr.includes('lenglen')) { courtKey = 'lenglen'; courtLabel = 'Lenglen'; }
        else if (lieuStr.includes('chatrier') || evStr.includes('chatrier')) { courtKey = 'chatrier'; courtLabel = 'Chatrier'; }
        else if (lieuStr.includes('outside') || lieuStr.includes('annexe')) { courtKey = 'outside'; courtLabel = 'Outside'; }
        else { courtKey = 'other'; courtLabel = ticket.lieu || '-'; }
        if (sessStr.includes('day') || evStr.includes('day session')) { sessionKey = 'day'; sessionLabel = 'Day Session'; }
        else if (sessStr.includes('evening') || sessStr.includes('night') || evStr.includes('evening session')) { sessionKey = 'evening'; sessionLabel = 'Evening Session'; }
      }
      
      const matchKey = isRG ? `${matchNum}|${courtKey}|${sessionKey}` : matchNum;
      
      if (!stats[matchKey]) {
        stats[matchKey] = {
          matchNum: matchNum,
          matchKey: matchKey,
          eventName: ticket.evenement,
          courtLabel,
          sessionLabel,
          totalQty: 0,
          totalPrice: 0,
          totalAttribue: 0,
          totalNonAttribue: 0,
          categories: {}
        };
      }
      
      const qty = parseInt(ticket.qty) || 1;
      const price = parseFloat(ticket.prix) || 0;
      // Si le billet a un ref_vente, prendre le prix de vente correspondant
      const refVente = ticket.ref_vente;
      const hasRefVente = refVente && refVente !== '' && refVente !== '-' && refVente !== 'N/A';
      const ventePrice = hasRefVente ? (ventePriceMap[refVente] || 0) : 0;
      const displayPrice = ventePrice > 0 ? ventePrice : price;
      // Détection attribution: a une ref_vente OU attribue=true OU statut MATCH_OK/SPECIAL_MATCH
      const isAttribue = ticket.attribue === true || hasRefVente || ticket.statut === 'MATCH_OK' || ticket.statut === 'SPECIAL_MATCH';
      
      stats[matchKey].totalQty += qty;
      stats[matchKey].totalPrice += displayPrice;
      grandTotalQty += qty;
      grandTotalPrice += displayPrice;
      
      // Compteurs attribués/non attribués
      if (isAttribue) {
        stats[matchKey].totalAttribue += qty;
        grandTotalAttribue += qty;
      } else {
        stats[matchKey].totalNonAttribue += qty;
        grandTotalNonAttribue += qty;
      }
      
      // Stats par catégorie
      const cat = ticket.categorie || 'N/A';
      if (!stats[matchKey].categories[cat]) {
        stats[matchKey].categories[cat] = { qty: 0, price: 0, attribue: 0, nonAttribue: 0 };
      }
      stats[matchKey].categories[cat].qty += qty;
      stats[matchKey].categories[cat].price += displayPrice;
      if (isAttribue) {
        stats[matchKey].categories[cat].attribue += qty;
      } else {
        stats[matchKey].categories[cat].nonAttribue += qty;
      }
    });
    
    // Trier par numéro de match
    const sortedMatches = Object.values(stats).sort((a, b) => {
      const numA = parseInt(a.matchNum.replace('M', '')) || 999;
      const numB = parseInt(b.matchNum.replace('M', '')) || 999;
      return numA - numB;
    });
    
    return { matches: sortedMatches, grandTotalQty, grandTotalPrice, grandTotalAttribue, grandTotalNonAttribue };
  }, [tickets, hasFilter, project, viewType]);
  
  // Ne rien afficher si pas de filtre actif ou pas de résultats
  if (!hasFilter || tickets.length === 0 || matchStats.matches.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-zinc-800/50 border border-[#C4A052]/30 rounded-lg p-4 mb-4" data-testid="filter-summary-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#C4A052] flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Récapitulatif {searchTerm && <span className="text-zinc-400">pour "{searchTerm}"</span>}
          </h3>
          {/* Bouton Masquer/Afficher */}
          <button
            onClick={() => {
              const next = !isCollapsed;
              localStorage.setItem('yas_recap_collapsed', next ? '1' : '0');
              setIsCollapsed(next);
            }}
            className="text-xs text-zinc-400 hover:text-[#C4A052] transition-colors flex items-center gap-1 ml-2"
          >
            {isCollapsed ? (
              <>
                <ChevronDown className="h-3 w-3" />
                Afficher
              </>
            ) : (
              <>
                <ChevronUp className="h-3 w-3" />
                Masquer
              </>
            )}
          </button>
          {/* Bouton Retour si un match est sélectionné */}
          {selectedMatchFilter && (
            <button
              onClick={onClearMatchFilter}
              className="text-xs bg-[#C4A052]/20 text-[#C4A052] px-2 py-1 rounded hover:bg-[#C4A052]/30 transition-colors flex items-center gap-1 ml-2"
            >
              <X className="h-3 w-3" />
              Retour (tous les matchs)
            </button>
          )}
        </div>
        <div className="text-sm font-bold text-white bg-[#C4A052]/20 px-3 py-1 rounded flex items-center gap-3">
          <span>TOTAL: {matchStats.grandTotalQty} billets</span>
          <span className="text-green-400">{matchStats.grandTotalAttribue} attrib.</span>
          <span className="text-orange-400">RESTE {matchStats.grandTotalNonAttribue}</span>
        </div>
      </div>
      
      {/* Contenu collapsible */}
      {!isCollapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {matchStats.matches.map(match => (
            <div 
              key={match.matchKey}
              onClick={() => onMatchClick?.(match.matchNum)}
              className={cn(
                "bg-zinc-900/80 border rounded-lg p-3 cursor-pointer transition-all hover:border-[#C4A052]/50 hover:bg-zinc-800/50",
                selectedMatchFilter === match.matchNum 
                  ? "border-[#C4A052] ring-1 ring-[#C4A052]/30" 
                  : "border-zinc-700"
              )}
            >
              {/* Header du match */}
              <div className="flex items-center justify-between mb-1">
                <span className={cn(
                  "font-bold px-2 py-0.5 rounded text-sm",
                  selectedMatchFilter === match.matchNum 
                    ? "bg-[#C4A052] text-black" 
                    : "bg-[#C4A052] text-black"
                )}>
                  {match.matchNum}
                </span>
                <span className="text-white font-semibold">
                  {match.totalQty} <span className="text-zinc-400 font-normal text-xs">billets</span>
                </span>
              </div>
              {/* Court + Session (RG) or event name */}
              {(match.courtLabel || match.sessionLabel) ? (
                <div className="text-[11px] mb-2 leading-tight flex items-center gap-1.5 flex-wrap" title={match.eventName}>
                  {match.courtLabel && (
                    <span className={cn(
                      "px-1.5 py-0.5 rounded text-[10px] font-semibold",
                      match.courtLabel === 'Lenglen' ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"
                    )}>{match.courtLabel}</span>
                  )}
                  {match.sessionLabel && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-zinc-700/60 text-zinc-300">
                      {match.sessionLabel}
                    </span>
                  )}
                </div>
              ) : match.eventName && (() => {
                const parts = match.eventName.split(' - ').map(p => p.trim());
                const name = parts.length >= 3 ? parts[2] : parts[1] || '';
                const session = parts.find(p => /session/i.test(p)) || '';
                const court = parts[parts.length - 1] || '';
                return (name || session) ? (
                  <div className="text-[10px] text-zinc-400 mb-2 leading-tight truncate" title={match.eventName}>
                    {name}{session ? ` · ${session}` : ''}{court && court !== name && court !== session ? ` · ${court}` : ''}
                  </div>
                ) : null;
              })()}
              
              {/* Compteurs Attribués / Reste */}
              <div className="flex items-center justify-between text-xs mb-2 pb-2 border-b border-zinc-700">
                <div className="flex items-center gap-1">
                  <span className="text-green-400 font-bold">{match.totalAttribue}</span>
                  <span className="text-zinc-500">attribués</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-zinc-500">RESTE</span>
                  <span className={cn(
                    "font-bold",
                    match.totalNonAttribue === 0 ? "text-green-400" : "text-orange-400"
                  )}>{match.totalNonAttribue}</span>
                </div>
              </div>
              
              {/* Détail par catégorie */}
              <div className="space-y-1">
                {Object.entries(match.categories)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([cat, data]) => (
                  <div key={cat} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">{cat}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">{data.attribue}</span>
                      <span className="text-zinc-500">/</span>
                      <span className="text-white font-medium">{data.qty}</span>
                      {data.nonAttribue > 0 && (
                        <span className="text-orange-400 text-[10px]">R:{data.nonAttribue}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Prix total du match */}
              <div className="mt-2 pt-2 border-t border-zinc-700 flex justify-between text-xs">
                <span className="text-zinc-500">Total</span>
                <span className="text-[#C4A052] font-semibold">{match.totalPrice.toFixed(2)} €</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TicketTable = ({ 
  tickets = [], 
  onUnmatch, 
  onDelete, 
  onBulkDelete,
  onEdit,
  onUpdateStatus,
  onMarkAttribue,
  onValidateTicket,
  onUnvalidate,        // NEW: rollback LIVRE → ATTRIBUE
  onMarkLivre,
  onAttribuerVente,  // NEW: callback for VENTE attribution modal
  onOpenProofModal,  // NEW: callback to open proof upload modal (VENTE)
  onOpenProofEnvoiModal,  // NEW: callback to open proof envoi modal (ACHAT)
  onUpdateClients,       // Callback to save inline client edits
  onBulkUpdated,         // NEW: notify parent after bulk-update (parent met à jour son state sans recharger)
  onToggleLitige,        // NEW: callback to toggle LITIGE flag (manual dispute)
  onToggleActionRequise, // NEW: callback to toggle ACTION REQUISE flag (manual yellow)
  loading = false,
  viewType = 'tout', // 'tout', 'achat', 'vente'
  globalSearchTerm = '',  // Global search term from parent
  onSearchTermChange,     // Callback to update global search term
  stats = null,           // Stats object with total counts
  isLightMode = false,    // Light mode flag - don't apply Tailwind bg classes in light mode
  isSilentRefresh = false,  // When true, don't reset pagination/filters (for auto-refresh)
  onFilteredTicketsChange,  // Callback to send filtered tickets to parent (for export)
  allFilterValues = null,   // NEW: Pre-loaded filter values from backend
  project = 'WORLD CUP 2026',  // Current project for conditional columns
  sourcePhones = {},  // Map of source -> phone number
  presorted = false,  // When true, skip default sorting (data is pre-grouped)
}) => {
  // Use global search term if provided, otherwise use local state
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const searchTerm = globalSearchTerm !== undefined && onSearchTermChange ? globalSearchTerm : localSearchTerm;
  const setSearchTerm = onSearchTermChange || setLocalSearchTerm;
  
  const [sortConfig, setSortConfig] = useState({ key: presorted ? '' : 'evenement', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [openSourceTooltip, setOpenSourceTooltip] = useState(null);
  const [editingClientsMap, setEditingClientsMap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const PAGE_OPTIONS = [100, 500, 1000, 2000, 5000];
  
  // Stadium plan modal state
  const [stadiumPlanOpen, setStadiumPlanOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [selectedMatchInfo, setSelectedMatchInfo] = useState(null);
  
  const onShowStadiumPlan = (stadium, matchInfo = null) => {
    setSelectedStadium(stadium);
    setSelectedMatchInfo(matchInfo);
    setStadiumPlanOpen(true);
  };
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState([]); // array of statuses; empty = all. e.g. ['attribue', 'valide']
  
  // Match filter from recap panel click
  const [recapMatchFilter, setRecapMatchFilter] = useState(null);
  
  // Column visibility state
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  
  // Column reorder state
  const [reorderMode, setReorderMode] = useState(false);
  const [columnOrder, setColumnOrder] = useState(null); // saved order from DB
  const [pendingOrder, setPendingOrder] = useState(null); // unsaved local changes
  const [columnWidths, setColumnWidths] = useState({}); // custom widths
  const [resizingCol, setResizingCol] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Load saved column order from backend
  React.useEffect(() => {
    if (!project) return;
    setColumnOrder(null);
    setPendingOrder(null);
    setColumnWidths({});
    setHiddenColumns(new Set()); // reset au changement de projet
    // 🛡️ Path relatif (au lieu de `${API_URL}/api/...`) — évite les erreurs CORS quand
    // REACT_APP_BACKEND_URL au build pointe vers un domain différent du runtime.
    // Sur Emergent, frontend + backend partagent le même origin via le proxy nginx.
    fetch(`/api/preferences/column-order/${encodeURIComponent(project)}/${viewType}`)
      .then(r => r.json())
      .then(data => { setColumnOrder(data.order || null); })
      .catch(() => {});
    fetch(`/api/preferences/column-widths/${encodeURIComponent(project)}/${viewType}`)
      .then(r => r.json())
      .then(data => { setColumnWidths(data.widths || {}); })
      .catch(() => {});
    // 🆕 Charge la visibilité des colonnes (colonnes masquées) — persiste entre les changements d'onglet
    fetch(`/api/preferences/column-visibility/${encodeURIComponent(project)}/${viewType}`)
      .then(r => r.json())
      .then(data => {
        const hidden = Array.isArray(data.hidden) ? data.hidden : [];
        setHiddenColumns(new Set(hidden));
      })
      .catch(() => {});
  }, [project, viewType, API_URL]);
  
  // 🆕 Auto-sauvegarde de la visibilité des colonnes dès qu'elle change.
  // Utilise un ref pour éviter de sauvegarder au mount initial (avant chargement).
  const hiddenColumnsLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (!project) return;
    if (!hiddenColumnsLoadedRef.current) {
      // Premier render après load : on marque comme "chargé" mais on NE sauvegarde PAS
      // (sinon on écraserait la valeur du backend par un Set vide momentané).
      hiddenColumnsLoadedRef.current = true;
      return;
    }
    fetch(`/api/preferences/column-visibility/${encodeURIComponent(project)}/${viewType}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hidden: Array.from(hiddenColumns) }),
    }).catch(() => {});
  }, [hiddenColumns, project, viewType]);

  // Reset du flag "loaded" quand on change de projet/vue → re-prêt à sauvegarder après chargement
  React.useEffect(() => {
    hiddenColumnsLoadedRef.current = false;
  }, [project, viewType]);

  // The active order: pending (unsaved) takes priority over saved
  const activeOrder = pendingOrder || columnOrder;
  
  // Move column left or right (local only, not saved yet)
  const moveColumn = (colKey, direction) => {
    const currentOrder = activeOrder || columns.map(c => c.key);
    const idx = currentOrder.indexOf(colKey);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= currentOrder.length) return;
    const newOrder = [...currentOrder];
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
    setPendingOrder(newOrder);
  };
  
  // Save column order to backend
  const saveColumnOrderToDb = () => {
    if (!pendingOrder) return;
    setColumnOrder(pendingOrder);
    setPendingOrder(null);
    setReorderMode(false);
    fetch(`/api/preferences/column-order/${encodeURIComponent(project)}/${viewType}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: pendingOrder })
    }).catch(() => {});
  };
  
  // Cancel reorder (discard unsaved changes)
  const cancelReorder = () => {
    setPendingOrder(null);
    setReorderMode(false);
  };
  
  // Reset column order
  const resetColumnOrder = () => {
    setColumnOrder(null);
    setPendingOrder(null);
    setReorderMode(false);
    fetch(`/api/preferences/column-order/${encodeURIComponent(project)}/${viewType}`, {
      method: 'DELETE'
    }).catch(() => {});
  };
  
  // Column resize handlers
  const handleResizeStart = useCallback((e, colKey, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingCol(colKey);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = parseInt(currentWidth) || 100;
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - resizeStartX.current;
      const newWidth = Math.max(50, resizeStartWidth.current + diff);
      setColumnWidths(prev => ({ ...prev, [colKey]: `${newWidth}px` }));
    };
    
    const handleMouseUp = () => {
      setResizingCol(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Save widths to backend
      setColumnWidths(prev => {
        fetch(`/api/preferences/column-widths/${encodeURIComponent(project)}/${viewType}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ widths: prev })
        }).catch(() => {});
        return prev;
      });
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [API_URL, project, viewType]);
  
  // Column filters state - stores filter value(s) for each column (supports multi-select as array)
  // 🛠️ FIX FEV 2026 : filtres SÉPARÉS par viewType (TOUT/ACHAT/VENTE/...) pour éviter qu'un filtre
  // sur la colonne 'block' appliqué dans ACHAT cache 80% des VENTE qui n'ont pas de block.
  // Avant : `yas_col_filters_${project}` (partagé) → 587 lignes / 4633 total côté VENTE.
  // Après : `yas_col_filters_${project}_${viewType}` → chaque onglet a son propre filtre persisté.
  const filtersStorageKey = `yas_col_filters_${project || 'default'}_${viewType || 'tout'}`;
  // 🧹 Migration one-shot : nettoie l'ancienne clé partagée pour que les filtres ne fuitent
  // plus entre les onglets (cf bug "587 lignes / 4633 total" — filtres ACHAT cachaient les VENTE).
  useEffect(() => {
    try {
      const legacyKey = `yas_col_filters_${project || 'default'}`;
      if (localStorage.getItem(legacyKey) !== null) {
        localStorage.removeItem(legacyKey);
      }
    } catch { /* ignore */ }
  }, [project]);
  const [columnFilters, setColumnFiltersInternal] = useState(() => {
    try {
      const raw = localStorage.getItem(filtersStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const setColumnFilters = (updater) => {
    setColumnFiltersInternal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem(filtersStorageKey, JSON.stringify(next));
        // Broadcast aux autres onglets (autres instances de TicketTable montées en parallèle)
        window.dispatchEvent(new CustomEvent('yas_col_filters_sync', { detail: { key: filtersStorageKey, value: next } }));
      } catch { /* ignore */ }
      return next;
    });
  };
  // Sync : écoute des modifications du localStorage par d'autres onglets
  useEffect(() => {
    const onSync = (e) => {
      if (e?.detail?.key === filtersStorageKey) {
        setColumnFiltersInternal(e.detail.value);
      }
    };
    window.addEventListener('yas_col_filters_sync', onSync);
    return () => window.removeEventListener('yas_col_filters_sync', onSync);
  }, [filtersStorageKey]);
  
  // Bulk edit modal state
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  // Quick placement (line-by-line) state
  const [quickPlacementPreview, setQuickPlacementPreview] = useState(null);
  const [quickPlacementLoading, setQuickPlacementLoading] = useState(false);
  
  // Project detection for conditional labels
  const isWC = project && project.toUpperCase().startsWith('WORLD CUP');
  const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
  
  // Fields available for bulk edit
  const BULK_EDIT_FIELDS = [
    { key: 'ref_achat', label: isWC ? 'Acheté le' : 'Réf Achat' },
    { key: 'evenement', label: 'Match / Événement' },
    { key: 'categorie', label: 'Catégorie' },
    { key: 'session', label: 'Session' },
    { key: 'source', label: 'Source / Plateforme' },
    { key: 'lieu', label: isRG ? 'Court' : 'Lieu' },
    { key: 'ville', label: 'Ville' },
    { key: 'pays', label: 'Pays' },
    { key: 'date_evenement', label: 'Date événement' },
    { key: 'date_operation', label: "Date d'achat" },
    { key: 'nom_client', label: 'Nom client' },
    { key: 'mail_client', label: 'Email client' },
    { key: 'email_compte', label: 'E-mail Compte' },
    { key: 'pw_compte', label: 'PW Compte' },
    { key: 'statut', label: 'Statut' },
    { key: 'note', label: 'Note' },
    { key: 'prix', label: 'Prix' },
    { key: 'qty', label: 'Quantité' },
    { key: 'block', label: 'Block' },
    { key: 'rang', label: 'Rang' },
    { key: 'place', label: 'Place' },
    { key: 'placement_rapide', label: '🎯 Emplacement rapide (ligne par ligne)' },
    { key: 'mode_livraison', label: 'Mode livraison' },
    { key: 'plan', label: 'Plan du stade' },
  ];
  
  // Helper: Get tickets filtered by all column filters EXCEPT the specified one
  // This enables cascading filters - show only relevant values based on other active filters
  const getTicketsFilteredExcept = React.useCallback((excludeColumnKey) => {
    let result = [...tickets];
    
    // Apply status filter
    if (statusFilter.length > 0) {
      result = result.filter(ticket => {
        const isVente = ticket.type === 'VENTE';
        const isAttribue = isVente 
          ? ticket.attribue === true
          : (ticket.attribue === true || (ticket.ref_vente && ticket.ref_vente.trim() !== ''));
        const statutUpper = (ticket.statut || '').toUpperCase();
        const isLivreOrValide = statutUpper === 'VALIDE' || statutUpper === 'LIVRE';
        const isSpecial = statutUpper === 'SPECIAL_MATCH';
        const isAnnule = statutUpper === 'ANNULEE' || statutUpper === 'ANNULÉ' || statutUpper === 'ANNULE';
        
        return statusFilter.some(f => {
          switch (f) {
            case 'attribue':
              return isAttribue && !isLivreOrValide && !isSpecial && !isAnnule;
            case 'non_attribue':
              return !isAttribue && !isLivreOrValide && !isAnnule;
            case 'valide':
              return isLivreOrValide;
            case 'special':
              return isSpecial;
            case 'annule':
              return isAnnule;
            default:
              return false;
          }
        });
      });
    }
    
    // Special marker for empty values in filters
    const EMPTY_VALUE_MARKER = '__VIDE__';
    
    // Apply all column filters EXCEPT the excluded one
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (columnKey === excludeColumnKey) return; // Skip the excluded column
      if (filterValue !== undefined && filterValue !== null) {
        if (Array.isArray(filterValue) && filterValue.length === 0) return;
        
        result = result.filter(ticket => {
          let ticketValue = ticket[columnKey];
          // For date_achat, also check date_operation
          if (columnKey === 'date_achat' && !ticketValue) ticketValue = ticket.date_operation;
          // For date_attribution, convert ISO → DD/MM/YYYY for comparison
          if (columnKey === 'date_attribution' && ticketValue) {
            try {
              const d = new Date(ticketValue);
              if (!isNaN(d.getTime())) {
                const pad = (n) => String(n).padStart(2, '0');
                ticketValue = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
              }
            } catch {}
          }
          const ticketValueStr = String(ticketValue || '').toLowerCase();
          
          if (Array.isArray(filterValue)) {
            return filterValue.some(fv => {
              // Handle "(Vide)" filter - match empty, null, or undefined values
              if (fv === EMPTY_VALUE_MARKER) {
                return ticketValue === undefined || ticketValue === null || ticketValue === '';
              }
              return ticketValueStr.includes(String(fv).toLowerCase());
            });
          }
          // Handle single value filter
          if (filterValue === EMPTY_VALUE_MARKER) {
            return ticketValue === undefined || ticketValue === null || ticketValue === '';
          }
          return ticketValueStr.includes(String(filterValue).toLowerCase());
        });
      }
    });
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(ticket => {
        return Object.values(ticket).some(val => 
          val && String(val).toLowerCase().includes(term)
        );
      });
    }
    
    return result;
  }, [tickets, statusFilter, columnFilters, searchTerm]);
  
  // Clear all column filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setStatusFilter([]);
    setRecapMatchFilter(null);
    setCurrentPage(1);
  };
  
  // Check if any filter is active (including search term)
  const hasActiveFilters = useMemo(() => {
    const hasColumnFilter = Object.values(columnFilters).some(v => 
      v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
    );
    return statusFilter.length > 0 || hasColumnFilter || !!searchTerm || !!recapMatchFilter;
  }, [statusFilter, columnFilters, searchTerm, recapMatchFilter]);
  
  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };
  
  const columns = useMemo(() => {
    switch (viewType) {
      case 'achat':
        return getAchatColumns(project);
      case 'vente':
        return getVenteColumns(project);
      default:
        return getToutColumns(project);
    }
  }, [viewType, project]);
  
  // Get visible columns with custom order (must be after columns declaration)
  const visibleColumns = useMemo(() => {
    let cols = columns.filter(col => !hiddenColumns.has(col.key));
    if (activeOrder) {
      const orderMap = {};
      activeOrder.forEach((key, i) => { orderMap[key] = i; });
      cols = [...cols].sort((a, b) => {
        const idxA = orderMap[a.key] ?? 9999;
        const idxB = orderMap[b.key] ?? 9999;
        return idxA - idxB;
      });
    }
    return cols;
  }, [columns, hiddenColumns, activeOrder]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const filteredAndSortedTickets = useMemo(() => {
    let result = [...tickets];

    // Apply status filter FIRST
    if (statusFilter.length > 0) {
      result = result.filter(ticket => {
        const isVente = ticket.type === 'VENTE';
        const isAttribue = isVente 
          ? ticket.attribue === true
          : (ticket.attribue === true || (ticket.ref_vente && ticket.ref_vente.trim() !== ''));
        const statutUpper = (ticket.statut || '').toUpperCase();
        const isLivreOrValide = statutUpper === 'VALIDE' || statutUpper === 'LIVRE';
        const isSpecial = statutUpper === 'SPECIAL_MATCH';
        const isAnnule = statutUpper === 'ANNULEE' || statutUpper === 'ANNULÉ' || statutUpper === 'ANNULE';
        
        return statusFilter.some(f => {
          switch (f) {
            case 'attribue':
              return isAttribue && !isLivreOrValide && !isSpecial && !isAnnule;
            case 'non_attribue':
              return !isAttribue && !isLivreOrValide && !isAnnule;
            case 'valide':
              return isLivreOrValide;
            case 'special':
              return isSpecial;
            case 'annule':
              return isAnnule;
            default:
              return false;
          }
        });
      });
    }
    
    // Special marker for empty values in filters
    const EMPTY_VALUE_MARKER = '__VIDE__';
    
    // Apply column filters (supports multi-select arrays)
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (filterValue !== undefined && filterValue !== null) {
        // Skip empty arrays (no filter selected)
        if (Array.isArray(filterValue) && filterValue.length === 0) {
          return; // Skip this filter
        }
        
        result = result.filter(ticket => {
          let ticketValue = ticket[columnKey];
          if (columnKey === 'date_achat' && !ticketValue) ticketValue = ticket.date_operation;
          // For date_attribution, convert ISO → DD/MM/YYYY for comparison
          if (columnKey === 'date_attribution' && ticketValue) {
            try {
              const d = new Date(ticketValue);
              if (!isNaN(d.getTime())) {
                const pad = (n) => String(n).padStart(2, '0');
                ticketValue = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
              }
            } catch {}
          }
          const ticketValueStr = String(ticketValue || '').toLowerCase();
          
          // Multi-select: filterValue is an array
          if (Array.isArray(filterValue)) {
            return filterValue.some(fv => {
              // Handle "(Vide)" filter - match empty, null, or undefined values
              if (fv === EMPTY_VALUE_MARKER) {
                return ticketValue === undefined || ticketValue === null || ticketValue === '';
              }
              return ticketValueStr.includes(String(fv).toLowerCase());
            });
          }
          
          // Single value (backward compatibility)
          if (filterValue === EMPTY_VALUE_MARKER) {
            return ticketValue === undefined || ticketValue === null || ticketValue === '';
          }
          return ticketValueStr.includes(String(filterValue).toLowerCase());
        });
      }
    });

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      
      // Special handling for match number search (M + number with 1-3 digits only)
      // Long numbers like "635867081" should search in ref_vente, not as match numbers
      const matchNumberSearch = term.match(/^m(\d{1,3})$/i) || (term.match(/^(\d{1,3})$/) && term.length <= 3);
      
      result = result.filter(ticket => {
        // If searching for a match number (like "M76" or "76" with max 3 digits), search in event name
        if (matchNumberSearch) {
          const searchNum = matchNumberSearch[1] || term;
          const eventName = ticket.evenement?.toLowerCase() || '';
          const matchPattern = new RegExp(`\\bm?${searchNum}\\b`, 'i');
          return matchPattern.test(eventName);
        }
        
        // Standard search: check all text fields including ref_achat and ref_vente
        const matchesEvenement = ticket.evenement?.toLowerCase().includes(term);
        const matchesRefAchat = ticket.ref_achat?.toLowerCase().includes(term);
        const matchesRefVente = ticket.ref_vente?.toLowerCase().includes(term);
        const matchesSource = ticket.source?.toLowerCase().includes(term);
        const matchesNomClient = ticket.nom_client?.toLowerCase().includes(term);
        const matchesMailClient = ticket.mail_client?.toLowerCase().includes(term);
        const matchesEmailCompte = ticket.email_compte?.toLowerCase().includes(term);
        const matchesLinkedMailCompte = ticket.linked_mail_du_compte?.toLowerCase().includes(term);
        const matchesLieu = ticket.lieu?.toLowerCase().includes(term);
        const matchesCategorie = ticket.categorie?.toLowerCase().includes(term);
        const matchesVille = ticket.ville?.toLowerCase().includes(term);
        const matchesPays = ticket.pays?.toLowerCase().includes(term);
        const matchesNote = ticket.note?.toLowerCase().includes(term);
        const matchesStatut = ticket.statut?.toLowerCase().includes(term);
        
        // Recherche dans le tableau `clients` (noms / emails / dob individuels des billets)
        // C'est ce qui permet de trouver "ISHAI Baruch" ou "ker.baruch@gmail.com" sans devoir
        // déplier chaque billet manuellement.
        let matchesClients = false;
        if (Array.isArray(ticket.clients) && ticket.clients.length > 0) {
          matchesClients = ticket.clients.some(c => {
            if (!c) return false;
            const nom = (c.nom || '').toLowerCase();
            const email = (c.email || '').toLowerCase();
            const dob = (c.dob || '').toLowerCase();
            return nom.includes(term) || email.includes(term) || dob.includes(term);
          });
        }
        
        return matchesEvenement || matchesRefAchat || matchesRefVente || 
               matchesSource || matchesNomClient || matchesMailClient || matchesEmailCompte ||
               matchesLinkedMailCompte || matchesLieu || matchesCategorie || matchesVille || 
               matchesPays || matchesNote || matchesStatut || matchesClients;
      });
    }
    
    // Apply recap match filter (when clicking on a match in the summary panel)
    if (recapMatchFilter) {
      result = result.filter(ticket => {
        const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
        const ticketMatchKey = matchNumMatch ? `M${matchNumMatch[1]}` : 'Autre';
        return ticketMatchKey === recapMatchFilter;
      });
    }

    // Tri secondaire par place pour Roland Garros (ACHAT et TOUT)
    const isRGWithPlace = (viewType === 'achat' || viewType === 'tout') && project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
    // Tri secondaire pour World Cup (et autres) : block → rang → place (numérique)
    const isWCWithPlace = (viewType === 'achat' || viewType === 'tout') && !isRGWithPlace;

    // Helper: multi-level RG sort (accès → escalier → rang → place)
    const rgSecondarySort = (a, b) => {
      const aAcces = (a.acces || '').toString();
      const bAcces = (b.acces || '').toString();
      if (aAcces !== bAcces) return aAcces.localeCompare(bAcces, undefined, { numeric: true });
      const aEsc = (a.escalier || '').toString();
      const bEsc = (b.escalier || '').toString();
      if (aEsc !== bEsc) return aEsc.localeCompare(bEsc, undefined, { numeric: true });
      const aRang = parseInt(a.rang, 10) || 99999;
      const bRang = parseInt(b.rang, 10) || 99999;
      if (aRang !== bRang) return aRang - bRang;
      const aPlace = parseInt(a.place, 10) || 99999;
      const bPlace = parseInt(b.place, 10) || 99999;
      return aPlace - bPlace;
    };

    // Helper: WC sort — block → rang → place (numérique strict)
    const wcSecondarySort = (a, b) => {
      const aBlock = parseInt(a.block, 10);
      const bBlock = parseInt(b.block, 10);
      const aBlockN = isNaN(aBlock) ? 99999 : aBlock;
      const bBlockN = isNaN(bBlock) ? 99999 : bBlock;
      if (aBlockN !== bBlockN) return aBlockN - bBlockN;
      // fallback alphabétique si non-numérique
      if (isNaN(aBlock) && isNaN(bBlock)) {
        const c = String(a.block || '').localeCompare(String(b.block || ''), undefined, { numeric: true });
        if (c !== 0) return c;
      }
      const aRang = parseInt(a.rang, 10);
      const bRang = parseInt(b.rang, 10);
      const aRangN = isNaN(aRang) ? 99999 : aRang;
      const bRangN = isNaN(bRang) ? 99999 : bRang;
      if (aRangN !== bRangN) return aRangN - bRangN;
      const aPlace = parseInt(a.place, 10);
      const bPlace = parseInt(b.place, 10);
      const aPlaceN = isNaN(aPlace) ? 99999 : aPlace;
      const bPlaceN = isNaN(bPlace) ? 99999 : bPlace;
      return aPlaceN - bPlaceN;
    };

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'ref') {
          aVal = a.type === 'ACHAT' ? a.ref_achat : a.ref_vente;
          bVal = b.type === 'ACHAT' ? b.ref_achat : b.ref_vente;
        }

        // Sort by match number for evenement
        if (sortConfig.key === 'evenement') {
          aVal = extractMatchNum(a.evenement);
          bVal = extractMatchNum(b.evenement);
        }

        // ✅ TRI ISO datetime pour date_attribution (2026-05-08T15:30:00Z)
        if (sortConfig.key === 'date_attribution') {
          const ta = aVal ? new Date(aVal).getTime() : 0;
          const tb = bVal ? new Date(bVal).getTime() : 0;
          if (ta < tb) return sortConfig.direction === 'asc' ? -1 : 1;
          if (ta > tb) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        // ✅ TRI PAR DATE - Convertir DD/MM/YY ou DD/MM/YYYY en date réelle
        if (sortConfig.key === 'date_event' || sortConfig.key === 'date_vente' || sortConfig.key === 'date_evenement' || sortConfig.key === 'date_operation' || sortConfig.key === 'date_achat') {
          const parseDate = (dateStr) => {
            if (!dateStr) return new Date(0);
            const str = String(dateStr).trim();
            
            // Extract date from string with prefix/suffix: "lun. 29/06/2026 15:30" → "29/06/2026"
            const embedded = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (embedded) {
              const day = parseInt(embedded[1], 10);
              const month = parseInt(embedded[2], 10) - 1;
              let year = parseInt(embedded[3], 10);
              if (year < 100) year = year < 50 ? 2000 + year : 1900 + year;
              return new Date(year, month, day);
            }
            
            // Format DD/MM (sans année) → 2026
            const noYear = str.match(/^(\d{1,2})\/(\d{1,2})$/);
            if (noYear) {
              return new Date(2026, parseInt(noYear[2], 10) - 1, parseInt(noYear[1], 10));
            }
            
            // Format DD-MM ou DD-MM-YYYY
            const dash = str.match(/^(\d{1,2})-(\d{1,2})(?:-(\d{2,4}))?$/);
            if (dash) {
              let year = dash[3] ? parseInt(dash[3], 10) : 2026;
              if (year < 100) year = year < 50 ? 2000 + year : 1900 + year;
              return new Date(year, parseInt(dash[2], 10) - 1, parseInt(dash[1], 10));
            }
            
            // ISO YYYY-MM-DD
            const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) {
              return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
            }
            
            return new Date(0);
          };
          
          aVal = parseDate(aVal);
          bVal = parseDate(bVal);
          
          // Comparer les timestamps
          const aTime = aVal.getTime();
          const bTime = bVal.getTime();
          if (aTime < bTime) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aTime > bTime) return sortConfig.direction === 'asc' ? 1 : -1;
          // Tri secondaire par emplacement pour RG ACHAT
          if (isRGWithPlace) {
            return rgSecondarySort(a, b);
          }
          return 0;
        }

        // ✅ TRI NATUREL pour ref_achat et ref_vente (NELS-85 < NELS-100)
        if (sortConfig.key === 'ref_achat' || sortConfig.key === 'ref_vente' || sortConfig.key === 'ref') {
          const naturalSort = (strA, strB) => {
            const a = String(strA || '');
            const b = String(strB || '');
            // Extract prefix and number: "NELS-85" → ["NELS", 85]
            const matchA = a.match(/^(.*?)(\d+)$/);
            const matchB = b.match(/^(.*?)(\d+)$/);
            if (matchA && matchB) {
              const prefixCmp = matchA[1].localeCompare(matchB[1]);
              if (prefixCmp !== 0) return prefixCmp;
              return parseInt(matchA[2]) - parseInt(matchB[2]);
            }
            return a.localeCompare(b);
          };
          const cmp = naturalSort(aVal, bVal);
          if (cmp !== 0) return sortConfig.direction === 'asc' ? cmp : -cmp;
          if (isRGWithPlace) return rgSecondarySort(a, b);
          return 0;
        }

        // ✅ TRI NUMÉRIQUE pour prix, quantité et placement (block/rang/siege)
        if (sortConfig.key === 'prix' || sortConfig.key === 'qty' || sortConfig.key === 'total') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        if (sortConfig.key === 'block' || sortConfig.key === 'rang' || sortConfig.key === 'place') {
          // Place / block / rang : tri numérique si possible, sinon alphabétique
          const an = parseInt(aVal, 10);
          const bn = parseInt(bVal, 10);
          const aIsNum = !isNaN(an);
          const bIsNum = !isNaN(bn);
          if (aIsNum && bIsNum) { aVal = an; bVal = bn; }
          else if (aIsNum && !bIsNum) return sortConfig.direction === 'asc' ? -1 : 1;
          else if (!aIsNum && bIsNum) return sortConfig.direction === 'asc' ? 1 : -1;
          else { aVal = String(aVal || ''); bVal = String(bVal || ''); }
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        // Tri secondaire par emplacement
        if (isRGWithPlace) return rgSecondarySort(a, b);
        if (isWCWithPlace) return wcSecondarySort(a, b);
        return 0;
      });
    } else if (!presorted) {
      // Default sort by event match number, avec tri secondaire par place
      result.sort((a, b) => {
        const primaryDiff = extractMatchNum(a.evenement) - extractMatchNum(b.evenement);
        if (primaryDiff !== 0) return primaryDiff;
        if (isRGWithPlace) return rgSecondarySort(a, b);
        if (isWCWithPlace) return wcSecondarySort(a, b);
        return 0;
      });
    }

    return result;
  }, [tickets, searchTerm, sortConfig, statusFilter, columnFilters, recapMatchFilter, viewType, project, presorted]);

  // Track previous filtered tickets to avoid infinite loops
  const prevFilteredLengthRef = useRef(0);
  const prevFilteredIdsRef = useRef('');

  // Notify parent of filtered tickets for export functionality
  // FIXED: Use refs to prevent infinite loops
  React.useEffect(() => {
    try {
      if (onFilteredTicketsChange && filteredAndSortedTickets) {
        // Create a stable ID string to check if data actually changed
        const currentLength = filteredAndSortedTickets.length;
        const currentIds = filteredAndSortedTickets.slice(0, 10).map(t => t.id).join(',');
        
        // Only notify if the data actually changed
        if (currentLength !== prevFilteredLengthRef.current || currentIds !== prevFilteredIdsRef.current) {
          prevFilteredLengthRef.current = currentLength;
          prevFilteredIdsRef.current = currentIds;
          onFilteredTicketsChange(filteredAndSortedTickets);
        }
      }
    } catch (e) {
      console.error('Error notifying filtered tickets:', e);
    }
  }, [filteredAndSortedTickets]); // Remove onFilteredTicketsChange from deps to avoid loops

  // Pagination — for presorted views, build pages that never split groups
  const presortedPages = useMemo(() => {
    if (!presorted) return null;
    const all = filteredAndSortedTickets;
    const pages = [];
    let i = 0;
    while (i < all.length) {
      const pageStart = i;
      let pageCount = 0;
      // Fill page up to itemsPerPage, but never cut a group
      while (i < all.length) {
        const t = all[i];
        const groupSize = t._groupStart ? (t._groupSize || 1) : 1;
        const blockSize = t._groupStart ? groupSize : 1;
        // If adding this block would exceed the limit AND page is not empty, stop here
        if (pageCount > 0 && pageCount + blockSize > itemsPerPage && !t._inGroup) {
          break;
        }
        // Add entire group or single ticket
        if (t._groupStart) {
          i += groupSize;
          pageCount += groupSize;
        } else {
          i++;
          pageCount++;
        }
      }
      pages.push([pageStart, i]);
    }
    return pages;
  }, [filteredAndSortedTickets, itemsPerPage, presorted]);

  const totalPages = presortedPages
    ? presortedPages.length
    : Math.ceil(filteredAndSortedTickets.length / itemsPerPage);
  const startIndex = presortedPages
    ? (presortedPages[currentPage - 1] ? presortedPages[currentPage - 1][0] : 0)
    : (currentPage - 1) * itemsPerPage;
  const endIndex = presortedPages
    ? (presortedPages[currentPage - 1] ? presortedPages[currentPage - 1][1] : 0)
    : startIndex + itemsPerPage;
  const paginatedTickets = useMemo(() => {
    return filteredAndSortedTickets.slice(startIndex, endIndex);
  }, [filteredAndSortedTickets, startIndex, endIndex]);

  // Reset page when search or items per page changes (but NOT during silent refresh)
  React.useEffect(() => {
    if (!isSilentRefresh) {
      setCurrentPage(1);
    }
  }, [searchTerm, itemsPerPage]); // Remove isSilentRefresh from deps to avoid loops
  
  // Reset page when column filters change
  const columnFiltersCount = Object.keys(columnFilters).length;
  React.useEffect(() => {
    setCurrentPage(1);
  }, [columnFiltersCount]);
  
  // Ajuster la page si elle dépasse le nombre total de pages (après suppression de tickets)
  React.useEffect(() => {
    // Only adjust if totalPages is valid and page is out of range
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [totalPages]); // Only depend on totalPages, not currentPage to avoid loops
  
  // Envoyer les tickets filtrés au parent pour l'export
  // TOTALEMENT DÉSACTIVÉ - l'export sera géré différemment
  const filteredTicketsRef = React.useRef([]);
  
  // Stocker les tickets filtrés dans la ref (sans callback au parent)
  React.useEffect(() => {
    filteredTicketsRef.current = filteredAndSortedTickets;
  }, [filteredAndSortedTickets]);

  const formatPrice = (value, devise = 'EUR') => {
    if (!value && value !== 0) return '-';
    const symbol = devise === 'EUR' ? '€' : devise === 'GBP' ? '£' : '$';
    return `${Number(value).toFixed(2)} ${symbol}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const str = String(dateStr).trim();
      
      // Format DD/MM/YYYY — déjà correct
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
      
      // Format DD/MM/YY → DD/MM/20YY
      const frShort = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
      if (frShort) {
        return `${frShort[1].padStart(2, '0')}/${frShort[2].padStart(2, '0')}/20${frShort[3]}`;
      }
      
      // Format DD/MM (sans année) → DD/MM/2026
      const frNoYear = str.match(/^(\d{1,2})\/(\d{1,2})$/);
      if (frNoYear) {
        return `${frNoYear[1].padStart(2, '0')}/${frNoYear[2].padStart(2, '0')}/2026`;
      }
      
      // Format DD-MM (tiret, sans année) → DD/MM/2026
      const dashNoYear = str.match(/^(\d{1,2})-(\d{1,2})$/);
      if (dashNoYear) {
        return `${dashNoYear[1].padStart(2, '0')}/${dashNoYear[2].padStart(2, '0')}/2026`;
      }
      
      // Format DD-MM-YYYY ou DD-MM-YY → DD/MM/YYYY
      const dashFull = str.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
      if (dashFull) {
        const year = dashFull[3].length === 2 ? `20${dashFull[3]}` : dashFull[3];
        return `${dashFull[1].padStart(2, '0')}/${dashFull[2].padStart(2, '0')}/${year}`;
      }
      
      // Format ISO YYYY-MM-DD → DD/MM/YYYY
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
      }
      
      // Essayer de parser comme Date
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
      }
      
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const getCellValue = (ticket, columnKey) => {
    const isRGCell = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
    switch (columnKey) {
      case 'type':
        return <TypeBadge type={ticket.type} />;
      case 'ref':
        return <span className="font-mono text-xs">{ticket.type === 'ACHAT' ? (ticket.ref_achat || '-') : (ticket.ref_vente || '-')}</span>;
      case 'ref_achat':
        // Pour WC2026, ref_achat = date d'achat → normaliser
        const isWCRef = project && project.toUpperCase().startsWith('WORLD CUP');
        if (isWCRef && ticket.ref_achat) {
          return <span className="font-mono text-xs">{formatDate(ticket.ref_achat)}</span>;
        }
        return <span className="font-mono text-xs">{ticket.ref_achat || '-'}</span>;
      case 'ref_vente':
        return <span className="font-mono text-xs">{ticket.ref_vente || '-'}</span>;
      case 'ref_vente_attribuer':
        return <span className="font-mono text-xs">{ticket.ref_vente || '-'}</span>;
      case 'statut':
        return <StatusBadge status={ticket.statut} />;
      case 'evenement':
        // Pour Rugby : équipes en gras d'abord, poule dessous (sans numéro M)
        const isRugbyProj = project && project.toUpperCase().includes('RUGBY');
        if (isRugbyProj && ticket.evenement) {
          const rm = ticket.evenement.match(/^M\d+\s*-\(\s*([^)]*)\)\s*-\s*(.+)$/i);
          if (rm) {
            return (
              <div className="max-w-[220px]" title={ticket.evenement}>
                <span className="block truncate text-sm font-bold text-white leading-tight">{rm[2].trim()}</span>
                <span className="block text-[10px] text-[#C4A052] font-semibold">( {rm[1].trim()} )</span>
              </div>
            );
          }
        }
        // Pour Roland Garros: "M1 - Day 1 - 1st Round - Day Session - Chatrier" → "Day 1 - 1st Round"
        const isRG = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));
        let displayName = formatEventName(ticket.evenement);
        if (isRG && ticket.evenement) {
          const parts = ticket.evenement.split(' - ');
          if (parts.length >= 3) {
            displayName = `${parts[2]?.trim()} - ${parts[1]?.trim()}`;
          }
        }
        return <span className="truncate max-w-[200px] block text-sm" title={ticket.evenement}>{displayName}</span>;
      case 'date_evenement':
        return <span className="font-mono text-xs">{formatDate(ticket.date_evenement)}</span>;
      case 'date_operation':
        return <span className="font-mono text-xs">{formatDate(ticket.date_operation)}</span>;
      case 'date_achat':
        return <span className="font-mono text-xs">{formatDate(ticket.date_achat || ticket.date_operation)}</span>;
      case 'date_attribution': {
        if (!ticket.date_attribution) return <span className="text-xs text-zinc-600">-</span>;
        // ISO datetime → DD/MM/YYYY HH:mm
        try {
          const d = new Date(ticket.date_attribution);
          if (isNaN(d.getTime())) return <span className="font-mono text-xs">{ticket.date_attribution}</span>;
          const pad = (n) => String(n).padStart(2, '0');
          const dateStr = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
          const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
          return (
            <span className="font-mono text-xs whitespace-nowrap" title={ticket.date_attribution}>
              {dateStr} <span className="text-zinc-500">{timeStr}</span>
            </span>
          );
        } catch {
          return <span className="font-mono text-xs">{ticket.date_attribution}</span>;
        }
      }
      case 'lieu': {
        const lieuStr = String(ticket.lieu || '');
        const isLenglen = lieuStr.toLowerCase().includes('lenglen');
        return (
          <span
            className={cn(
              "truncate max-w-[120px] block text-xs font-semibold",
              isLenglen && "text-red-400"
            )}
            title={ticket.lieu}
          >
            {ticket.lieu || '-'}
          </span>
        );
      }
      case 'plan':
        const customPlan = ticket.preuve_envoi;
        const stadium = findStadium(ticket.lieu, ticket.ville, ticket.evenement, project);
        const hasPlan = customPlan || stadium;
        if (hasPlan) {
          const matchNumMatch = ticket.evenement?.match(/M(\d+)/i);
          const matchNum = matchNumMatch ? `M${matchNumMatch[1]}` : null;
          
          const planStadium = customPlan 
            ? { id: 'custom', name: ticket.lieu || 'Plan', city: ticket.ville || '', capacity: 0, mapUrl: customPlan }
            : stadium;
          
          const matchInfo = {
            matchNum: matchNum,
            eventName: ticket.evenement,
            date: ticket.date_evenement,
            categorie: ticket.categorie
          };
          
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onShowStadiumPlan) onShowStadiumPlan(planStadium, matchInfo);
              }}
              className="h-6 px-2 text-xs border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
            >
              <MapPin className="h-3 w-3 mr-1" />
              Voir
            </Button>
          );
        }
        return <span className="text-xs text-zinc-500">-</span>;
      case 'ville':
        return <span className="text-xs">{ticket.ville || '-'}</span>;
      case 'pays':
        return <span className="text-xs">{ticket.pays || '-'}</span>;
      case 'categorie': {
        // Si la catégorie est absente / "N/A" / "-", on construit un fallback à partir
        // du block et du rang (ex : "Bloc 102, Rang BB"). Permet d'éviter d'afficher "N/A"
        // alors que les infos de placement sont dispos sur la même ligne.
        const cat = (ticket.categorie || '').trim();
        const isMissing = !cat || /^(n\/?a|-)$/i.test(cat);
        if (isMissing) {
          const b = (ticket.block || '').trim();
          const r = (ticket.rang || '').trim();
          if (b || r) {
            const parts = [];
            if (b) parts.push(`Bloc ${b}`);
            if (r) parts.push(`Rang ${r}`);
            return <span className="text-xs text-zinc-300">{parts.join(', ')}</span>;
          }
          return <span className="text-xs">-</span>;
        }
        return <span className="text-xs">{cat}</span>;
      }
      case 'session':
        // Afficher Day/Evening avec un icône
        const sessionValue = ticket.session || '';
        const isEvening = sessionValue.toLowerCase().includes('evening') || sessionValue.toLowerCase().includes('night') || sessionValue.toLowerCase().includes('nuit');
        const isDay = sessionValue.toLowerCase().includes('day') || sessionValue.toLowerCase().includes('jour');
        if (isEvening) {
          return <span className="text-xs text-purple-400">🌙 Evening</span>;
        } else if (isDay) {
          return <span className="text-xs text-yellow-500">☀️ Day</span>;
        }
        return <span className="text-xs text-zinc-500">-</span>;
      case 'tribune':
        return <span className="text-xs text-white">{ticket.tribune || '-'}</span>;
      case 'block':
        return <span className="text-xs text-white">{ticket.block || ''}</span>;
      case 'acces':
        return <span className="text-xs text-white">{ticket.acces || '-'}</span>;
      case 'escalier':
        return <span className="text-xs text-white">{ticket.escalier || '-'}</span>;
      case 'rang':
        return <span className="text-xs text-white">{ticket.rang || ''}</span>;
      case 'place':
        return <span className="text-xs text-white">{ticket.place || ''}</span>;
      case 'qty':
        return <span className="font-mono text-center">{ticket.qty || 1}</span>;
      case 'prix':
        return <span className="font-mono text-xs">{formatPrice(ticket.prix, ticket.devise)}</span>;
      case 'source':
        const sourceInfo = sourcePhones[ticket.source] || {};
        const isTooltipOpen = openSourceTooltip === ticket.id;
        return (
          <div className="relative">
            <span
              className="text-xs flex items-center gap-1 cursor-pointer hover:text-[#C4A052] transition-colors"
              onClick={(e) => { e.stopPropagation(); setOpenSourceTooltip(isTooltipOpen ? null : ticket.id); }}
            >
              {ticket.source || '-'}
            </span>
            {isTooltipOpen && (
              <div className="absolute z-50 top-full left-0 mt-1 px-3 py-2.5 bg-zinc-900 border border-zinc-600 rounded-lg shadow-xl text-xs whitespace-nowrap space-y-1.5 min-w-[220px]" onClick={(e) => e.stopPropagation()}>
                <div className="text-white font-semibold border-b border-zinc-700 pb-1 mb-1">{ticket.source}</div>
                <div className="flex items-center gap-2 cursor-pointer hover:bg-zinc-800 rounded px-1 py-0.5 -mx-1" onClick={(e) => { e.stopPropagation(); if (sourceInfo.phone) { navigator.clipboard.writeText(sourceInfo.phone); import('sonner').then(m => m.toast.success('Tel copié !')); } }}>
                  <Phone className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  <span className={sourceInfo.phone ? 'text-emerald-400 font-medium' : 'text-zinc-600'}>{sourceInfo.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2 cursor-pointer hover:bg-zinc-800 rounded px-1 py-0.5 -mx-1" onClick={(e) => { e.stopPropagation(); if (sourceInfo.email) { navigator.clipboard.writeText(sourceInfo.email); import('sonner').then(m => m.toast.success('Email copié !')); } }}>
                  <Mail className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                  <span className={sourceInfo.email ? 'text-sky-400 font-medium' : 'text-zinc-600'}>{sourceInfo.email || '—'}</span>
                </div>
                <div className="flex items-center gap-2 hover:bg-zinc-800 rounded px-1 py-0.5 -mx-1">
                  <Link2 className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  {sourceInfo.sheet_url ? (
                    <a href={sourceInfo.sheet_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 font-medium hover:underline" onClick={(e) => e.stopPropagation()}>{sourceInfo.sheet_url.length > 35 ? sourceInfo.sheet_url.slice(0, 35) + '...' : sourceInfo.sheet_url}</a>
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case 'nom_client':
        // VENTE tickets RG only: always show client expand button
        if (ticket.type === 'VENTE' && isRGCell) {
          const clientCount = ticket.clients?.length || 0;
          const isExpanded = expandedRows.has(ticket.id);
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRows(prev => {
                  const next = new Set(prev);
                  if (next.has(ticket.id)) {
                    next.delete(ticket.id);
                    // Clear edit mode when collapsing
                    setEditingClientsMap(prev2 => {
                      const copy = { ...prev2 };
                      delete copy[ticket.id];
                      return copy;
                    });
                  } else {
                    next.add(ticket.id);
                    // Auto-enter edit mode if no clients
                    if (clientCount === 0) {
                      const qty = ticket.qty || 1;
                      const slots = Array.from({ length: qty }, () => ({ nom: '', dob: '', email: '' }));
                      setEditingClientsMap(prev2 => ({ ...prev2, [ticket.id]: slots }));
                    }
                  }
                  return next;
                });
              }}
              className={cn(
                "flex items-center gap-1 text-xs transition-colors",
                "text-cyan-400 hover:text-cyan-300"
              )}
              title={clientCount > 0 ? `${clientCount} client(s) - Cliquer pour déplier` : 'Aucun client - Cliquer pour ajouter'}
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              <Users className="h-3 w-3" />
              <span className={
                clientCount === 0
                  ? 'text-red-400'
                  : (ticket.clients || []).every(c => c.nom && c.nom.trim() && c.email && c.email.includes('@') && c.dob && c.dob.trim())
                    ? 'text-emerald-400'
                    : 'text-orange-400'
              }>
                {clientCount === 0
                  ? 'Need Détail'
                  : (ticket.clients || []).every(c => c.nom && c.nom.trim() && c.email && c.email.includes('@') && c.dob && c.dob.trim())
                    ? `${clientCount} Détail Ok`
                    : `${clientCount} Détail Incomplet`}
              </span>
            </button>
          );
        }
        // Tooltip enrichi si nouveau mail Viagogo détecté
        if (ticket.action_requise_new_email) {
          return <ActionRequiseEmailCell ticket={ticket} />;
        }
        return <span className="truncate max-w-[100px] block text-xs" title={ticket.nom_client}>{ticket.nom_client || '-'}</span>;
      case 'mail_client':
        return <span className="truncate max-w-[120px] block text-xs" title={ticket.mail_client}>{ticket.mail_client || '-'}</span>;
      case 'dob_client':
        return <span className="text-xs">{ticket.dob_client || '-'}</span>;
      case 'email_compte':
        // Pour VENTE, afficher linked_mail_du_compte si disponible
        const emailCompteValue = ticket.email_compte || ticket.linked_mail_du_compte || '-';
        const isLinked = !ticket.email_compte && ticket.linked_mail_du_compte;
        return <span className={`truncate max-w-[140px] block text-xs ${isLinked ? 'text-blue-400' : ''}`} title={emailCompteValue}>{emailCompteValue}</span>;
      case 'pw_compte':
        return <span className="text-xs font-mono">{ticket.pw_compte || '-'}</span>;
      case 'linked_mail_du_compte': {
        // Display linked ACHAT's mail_du_compte for VENTE tickets (multi-source = multi-line)
        const val = ticket.linked_mail_du_compte || '';
        if (!val) return <span className="text-xs text-zinc-500">-</span>;
        const lines = val.split('\n').filter(Boolean);
        return (
          <div className="text-xs text-blue-400 leading-tight max-w-[180px]" title={val}>
            {lines.map((l, i) => <div key={i} className="truncate">{l}</div>)}
          </div>
        );
      }
      case 'linked_pw_du_compte': {
        const val = ticket.linked_pw_du_compte || '';
        if (!val) return <span className="text-xs text-zinc-500">-</span>;
        const lines = val.split('\n').filter(Boolean);
        return (
          <div className="text-xs font-mono text-blue-400 leading-tight max-w-[140px]" title={val}>
            {lines.map((l, i) => <div key={i} className="truncate">{l}</div>)}
          </div>
        );
      }
      case 'linked_source_achat': {
        const val = ticket.linked_source_achat || '';
        if (!val) return <span className="text-xs text-zinc-500">-</span>;
        const lines = val.split('\n').filter(Boolean);
        return (
          <div className="text-xs text-blue-400 leading-tight font-semibold" title={val}>
            {lines.map((l, i) => <div key={i} className="truncate">{l}</div>)}
          </div>
        );
      }
      case 'preuve_envoi':
        // Pour les tickets ACHAT: bouton pour upload/voir la preuve d'envoi
        const hasPreuveEnvoi = ticket.preuve_envoi && ticket.preuve_envoi !== '';
        const isPreuveEnvoiUrl = hasPreuveEnvoi && (ticket.preuve_envoi.startsWith('http') || ticket.preuve_envoi.startsWith('/uploads'));
        
        if (isPreuveEnvoiUrl) {
          // Has uploaded proof - show "Voir" button
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenProofEnvoiModal) {
                  onOpenProofEnvoiModal(ticket);
                }
              }}
              className="h-6 text-xs px-2 bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30"
            >
              ✓ Voir
            </Button>
          );
        } else if (hasPreuveEnvoi) {
          // Legacy text link (not uploaded)
          return (
            <a 
              href={ticket.preuve_envoi} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gold hover:underline text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              Voir
            </a>
          );
        }
        
        // No proof - show "Ajouter" button
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenProofEnvoiModal) {
                onOpenProofEnvoiModal(ticket);
              }
            }}
            className="h-6 text-xs px-2 bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600"
          >
            📷 Ajouter
          </Button>
        );
      case 'preuve':
        // Colonne PREUVE unifiée - fonctionne pour ACHAT (preuve_envoi) et VENTE (preuve_url)
        if (ticket.type === 'ACHAT') {
          // ACHAT: utilise preuve_envoi
          const hasAchatPreuve = ticket.preuve_envoi && ticket.preuve_envoi !== '';
          const isAchatPreuveUrl = hasAchatPreuve && (ticket.preuve_envoi.startsWith('http') || ticket.preuve_envoi.startsWith('/uploads'));
          
          if (isAchatPreuveUrl) {
            return (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenProofEnvoiModal) {
                    onOpenProofEnvoiModal(ticket);
                  }
                }}
                className="h-6 text-xs px-2 bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30"
              >
                ✓ Voir
              </Button>
            );
          } else if (hasAchatPreuve) {
            return (
              <a 
                href={ticket.preuve_envoi} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gold hover:underline text-xs"
                onClick={(e) => e.stopPropagation()}
              >
                Voir
              </a>
            );
          }
          
          // No proof - show "Ajouter" button for ACHAT
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenProofEnvoiModal) {
                  onOpenProofEnvoiModal(ticket);
                }
              }}
              className="h-6 text-xs px-2 bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600"
            >
              📷 Ajouter
            </Button>
          );
        }
        
        // VENTE: utilise preuve_url
        const hasPreuve = ticket.preuve_url && ticket.preuve_url !== '';
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenProofModal) {
                onOpenProofModal(ticket);
              }
            }}
            className={`h-6 text-xs px-2 ${
              hasPreuve 
                ? 'bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30' 
                : 'bg-zinc-700/50 border-zinc-600 text-zinc-400 hover:bg-zinc-600'
            }`}
          >
            {hasPreuve ? '✓ Voir' : '📷 Ajouter'}
          </Button>
        );
      case 'note':
        return <span className="truncate max-w-[100px] block text-xs" title={ticket.note}>{ticket.note || '-'}</span>;
      case 'acheteur_original':
        return <span className="text-xs">{ticket.acheteur_original || '-'}</span>;
      case 'ref_vente':
        // For TOUT view - show ref_vente for both ACHAT and VENTE
        const refValue = ticket.ref_vente || ticket.ref_vente_attribuer || '';
        return <span className="text-xs font-mono text-blue-400">{refValue || '-'}</span>;
      case 'attribuer':
        const isCancelled = ticket.statut === 'ANNULEE';
        const isLivre = ticket.statut === 'LIVRE';
        const isValidated = ticket.statut === 'VALIDE';
        const isSpecialMatch = ticket.statut === 'SPECIAL_MATCH';
        const isVenteTicket = ticket.type === 'VENTE';
        const isAchatTicket = ticket.type === 'ACHAT';
        const isAttributed = isVenteTicket
          ? (ticket.attribue === true)
          : (ticket.attribue === true || 
             (ticket.ref_vente && ticket.ref_vente.trim() !== '') ||
             (ticket.ref_vente_attribuer && ticket.ref_vente_attribuer.trim() !== ''));
        const isPartial = ticket.attribution_status === 'partial';
        const isRGProject = project && (project.toUpperCase().includes('ROLAN') || project.toUpperCase().includes('RG'));

        // ===== ROLAND GARROS FLOW: NOIR → BLEU → VERT → ROUGE =====
        if (isRGProject) {
          // LIVRE (ROUGE) - final state, but can still désattribuer
          if (isLivre) {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" onClick={(e) => e.stopPropagation()}
                    className="h-6 text-xs px-2 bg-red-600 hover:bg-red-700 text-white font-semibold">
                    LIVRÉ
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <ActionRequiseMenuItem ticket={ticket} viewType={viewType} onToggleActionRequise={onToggleActionRequise} />
                  <DropdownMenuItem onClick={() => onMarkAttribue?.(ticket.id, false)} className="text-zinc-400">
                    <X className="h-4 w-4 mr-2" />
                    Désattribuer
                  </DropdownMenuItem>
                  <LitigeMenuItem ticket={ticket} viewType={viewType} onToggleLitige={onToggleLitige} />
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          // VALIDE (VERT) - validated, can only mark as delivered
          if (isValidated) {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" onClick={(e) => e.stopPropagation()}
                    className="h-6 text-xs px-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
                    ✓ Validé
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <ActionRequiseMenuItem ticket={ticket} viewType={viewType} onToggleActionRequise={onToggleActionRequise} />
                  <DropdownMenuItem onClick={() => onMarkLivre?.(ticket.id)} className="text-red-400">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Marquer Livré
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMarkAttribue?.(ticket.id, false)} className="text-zinc-400">
                    <X className="h-4 w-4 mr-2" />
                    Désattribuer
                  </DropdownMenuItem>
                  <LitigeMenuItem ticket={ticket} viewType={viewType} onToggleLitige={onToggleLitige} />
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          // ATTRIBUÉ (BLEU) - attributed, can validate or unattribute
          if (isAttributed || isSpecialMatch) {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" onClick={(e) => e.stopPropagation()}
                    className={cn("h-6 text-xs px-2 font-semibold",
                      isPartial ? "bg-sky-500 hover:bg-sky-600 text-white"
                      : isSpecialMatch ? "bg-amber-500 hover:bg-amber-600 text-black"
                      : "bg-blue-500 hover:bg-blue-600 text-white")}>
                    {isPartial ? (
                      isVenteTicket
                        ? `⚡ PARTIEL ${ticket.partial_qty || 0}/${ticket.qty || 1}`
                        : '⚡ PARTIEL'
                    ) : isSpecialMatch ? "✓ Spécial" : "✓ Attribué"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-52">
                  <ActionRequiseMenuItem ticket={ticket} viewType={viewType} onToggleActionRequise={onToggleActionRequise} />
                  <DropdownMenuItem onClick={() => onValidateTicket?.(ticket.id)} className="text-emerald-400">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Valider ✓
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMarkAttribue?.(ticket.id, false)} className="text-zinc-400">
                    <X className="h-4 w-4 mr-2" />
                    Désattribuer
                  </DropdownMenuItem>
                  <LitigeMenuItem ticket={ticket} viewType={viewType} onToggleLitige={onToggleLitige} />
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          // PAS ATTRIBUÉ (NOIR) - for VENTE: show "Attribuer →", for ACHAT: show "Non attr."
          if (isVenteTicket || viewType === 'vente') {
            return (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline"
                  onClick={(e) => { e.stopPropagation(); onAttribuerVente?.(ticket); }}
                  className="h-6 text-xs px-2 border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20 bg-zinc-800">
                  Attribuer →
                </Button>
                {isVenteTicket && (viewType === 'vente' || viewType === 'litige' || viewType === 'action-requise' || ticket.litige || ticket.action_requise) && (
                  <>
                    {onToggleLitige && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); onToggleLitige(ticket, !ticket.litige); }}
                        className={cn(
                          "h-6 w-6 p-0",
                          ticket.litige
                            ? "bg-purple-600 hover:bg-purple-700 text-white"
                            : "text-purple-400 hover:text-purple-200 hover:bg-purple-900/40"
                        )}
                        title={ticket.litige ? "Retirer du LITIGE" : "Mettre en LITIGE"}
                        data-testid={`btn-litige-rg-${ticket.id}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {onToggleActionRequise && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); onToggleActionRequise(ticket, !ticket.action_requise); }}
                        className={cn(
                          "h-6 w-6 p-0",
                          ticket.action_requise
                            ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                            : "text-yellow-400 hover:text-yellow-200 hover:bg-yellow-900/40"
                        )}
                        title={ticket.action_requise ? "Retirer ACTION REQUISE" : "Marquer ACTION REQUISE"}
                        data-testid={`btn-action-requise-rg-${ticket.id}`}
                      >
                        <Bell className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            );
          }
          // ACHAT in TOUT or ACHAT view
          return (
            <Button size="sm" variant="outline" disabled onClick={(e) => e.stopPropagation()}
              className="h-6 text-xs px-2 border-zinc-600 text-zinc-500 bg-zinc-800 cursor-not-allowed">
              Non attr.
            </Button>
          );
        }

        // ===== WORLD CUP / OTHER PROJECT FLOW (original behavior) =====
        if (isCancelled) {
          return (
            <Button size="sm" disabled onClick={(e) => e.stopPropagation()}
              className="h-6 text-xs px-2 bg-red-600 hover:bg-red-600 text-white font-semibold cursor-not-allowed">
              ✗ ANNULÉ
            </Button>
          );
        }
        if (isValidated) {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" onClick={(e) => e.stopPropagation()}
                  className="h-6 text-xs px-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
                  ✓ LIVRÉ
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <ActionRequiseMenuItem ticket={ticket} viewType={viewType} onToggleActionRequise={onToggleActionRequise} />
                <DropdownMenuItem onClick={() => onUnvalidate?.(ticket.id)} className="text-blue-400">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Repasser en ATTRIBUÉ
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMarkAttribue?.(ticket.id, false)} className="text-red-400">
                  <X className="h-4 w-4 mr-2" />
                  Désattribuer (annuler livraison)
                </DropdownMenuItem>
                <LitigeMenuItem ticket={ticket} viewType={viewType} onToggleLitige={onToggleLitige} />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        if (isAttributed || isSpecialMatch) {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" onClick={(e) => e.stopPropagation()}
                  className={cn("h-6 text-xs px-2 font-semibold",
                    isPartial ? "bg-sky-500 hover:bg-sky-600 text-white"
                    : isSpecialMatch ? "bg-amber-500 hover:bg-amber-600 text-black"
                    : "bg-blue-500 hover:bg-blue-600 text-white")}>
                  {isPartial ? (
                    isVenteTicket
                      ? `⚡ PARTIEL ${ticket.partial_qty || 0}/${ticket.qty || 1}`
                      : '⚡ PARTIEL'
                  ) : isSpecialMatch ? "✓ Spécial" : "✓ Attribué"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <ActionRequiseMenuItem ticket={ticket} viewType={viewType} onToggleActionRequise={onToggleActionRequise} />
                <DropdownMenuItem onClick={() => onMarkAttribue?.(ticket.id, false)} className="text-zinc-400">
                  <X className="h-4 w-4 mr-2" />
                  Désattribuer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onValidateTicket?.(ticket.id)} className="text-emerald-400">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Livrer ✓
                </DropdownMenuItem>
                <LitigeMenuItem ticket={ticket} viewType={viewType} onToggleLitige={onToggleLitige} />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }
        // Not attributed
        if (isVenteTicket || viewType === 'vente') {
          return (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline"
                onClick={(e) => { e.stopPropagation(); onAttribuerVente?.(ticket); }}
                className="h-6 text-xs px-2 border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20 bg-zinc-800">
                Attribuer →
              </Button>
              {isVenteTicket && (viewType === 'vente' || viewType === 'litige' || viewType === 'action-requise' || ticket.litige || ticket.action_requise) && (
                <>
                  {onToggleLitige && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); onToggleLitige(ticket, !ticket.litige); }}
                      className={cn(
                        "h-6 w-6 p-0",
                        ticket.litige
                          ? "bg-purple-600 hover:bg-purple-700 text-white"
                          : "text-purple-400 hover:text-purple-200 hover:bg-purple-900/40"
                      )}
                      title={ticket.litige ? "Retirer du LITIGE" : "Mettre en LITIGE"}
                      data-testid={`btn-litige-${ticket.id}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {onToggleActionRequise && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); onToggleActionRequise(ticket, !ticket.action_requise); }}
                      className={cn(
                        "h-6 w-6 p-0",
                        ticket.action_requise
                          ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                          : "text-yellow-400 hover:text-yellow-200 hover:bg-yellow-900/40"
                      )}
                      title={ticket.action_requise ? "Retirer ACTION REQUISE" : "Marquer ACTION REQUISE"}
                      data-testid={`btn-action-requise-${ticket.id}`}
                    >
                      <Bell className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          );
        }
        if (isAchatTicket) {
          return (
            <Button size="sm" variant="outline" disabled onClick={(e) => e.stopPropagation()}
              className="h-6 text-xs px-2 border-zinc-600 text-zinc-500 bg-zinc-800 cursor-not-allowed">
              Non attr.
            </Button>
          );
        }
        return (
          <Button size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); onAttribuerVente?.(ticket); }}
            className="h-6 text-xs px-2 border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20 bg-zinc-800">
            Attribuer →
          </Button>
        );
      default:
        return ticket[columnKey] || '-';
    }
  };

  // Selection functions - only for current page
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedTickets.length && paginatedTickets.every(t => selectedIds.has(t.id))) {
      // Deselect current page
      const newSelected = new Set(selectedIds);
      paginatedTickets.forEach(t => newSelected.delete(t.id));
      setSelectedIds(newSelected);
    } else {
      // Select current page
      const newSelected = new Set(selectedIds);
      paginatedTickets.forEach(t => newSelected.add(t.id));
      setSelectedIds(newSelected);
    }
  };

  const selectAllFiltered = () => {
    if (selectedIds.size === filteredAndSortedTickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedTickets.map(t => t.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (window.confirm(`Supprimer ${selectedIds.size} billet(s) sélectionné(s) ?`)) {
      onBulkDelete?.(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  // Bulk désattribuer function
  const handleBulkDesattribuer = async () => {
    if (selectedIds.size === 0) return;
    
    const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
    const attribuesCount = selectedTickets.filter(t => t.attribue || t.ref_vente).length;
    
    if (attribuesCount === 0) {
      alert('Aucun billet attribué dans la sélection');
      return;
    }
    
    if (!window.confirm(`Désattribuer ${attribuesCount} billet(s) attribué(s) sur ${selectedIds.size} sélectionné(s) ?\n\nCela va:\n- Effacer ref_vente, nom_client, mail_client\n- Remettre le statut à vide\n- Libérer les billets pour une nouvelle attribution`)) {
      return;
    }
    
    try {
      const API_URL = '';
      let successCount = 0;
      let errorCount = 0;
      
      // Désattribuer chaque ticket sélectionné qui est attribué
      for (const ticket of selectedTickets) {
        if (ticket.attribue || ticket.ref_vente) {
          try {
            const response = await fetch(`/api/tickets/${ticket.id}/attribue?attribue=false`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
            
            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (e) {
            errorCount++;
          }
        }
      }
      
      // Clear selection
      setSelectedIds(new Set());
      
      // Show result
      if (errorCount === 0) {
        alert(`✅ ${successCount} billet(s) désattribué(s) avec succès`);
      } else {
        alert(`⚠️ ${successCount} réussi(s), ${errorCount} erreur(s)`);
      }
      
      // Trigger refresh via window reload or callback
      window.location.reload();
    } catch (error) {
      console.error('Bulk desattribuer error:', error);
      alert(`Erreur: ${error.message}`);
    }
  };

  // Copier la sélection dans le presse-papier (format tableur)
  const handleCopySelection = () => {
    if (selectedIds.size === 0) return;
    
    const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
    
    // En-têtes
    const headers = [
      'REF_VENTE', 'DATE', 'SOURCE', 'MATCH', 'LIEU', 'VILLE', 'PAYS', 
      'DATE_MATCH', 'CATEGORIE', 'QTY', 'PRIX', 'NOM_CLIENT', 'MAIL_CLIENT',
      'EMAIL_COMPTE', 'BLOCK', 'RANG', 'PLACE', 'NOTE', 'STATUT'
    ];
    
    // Données
    const rows = selectedTickets.map(t => [
      t.ref_vente || '',
      t.date_operation || '',
      t.source || '',
      t.evenement || '',
      t.lieu || '',
      t.ville || '',
      t.pays || '',
      t.date_evenement || '',
      t.categorie || '',
      t.qty || '',
      t.prix || '',
      t.nom_client || '',
      t.mail_client || '',
      t.email_compte || '',
      t.block || '',
      t.rang || '',
      t.place || '',
      t.note || '',
      t.statut || ''
    ].join('\t')).join('\n');
    
    const clipboardText = headers.join('\t') + '\n' + rows;
    
    navigator.clipboard.writeText(clipboardText).then(() => {
      alert(`✅ ${selectedTickets.length} ligne(s) copiée(s) !\n\nCollez dans Google Sheets ou Excel.`);
    }).catch(err => {
      console.error('Erreur copie:', err);
      alert('Erreur lors de la copie');
    });
  };

  // Bulk diffusion publique (toggle on/off selon majorité)
  const handleBulkDiffuse = async () => {
    if (selectedIds.size === 0) return;
    const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
    const onlyAchats = selectedTickets.filter(t => t.type === 'ACHAT');
    if (onlyAchats.length === 0) {
      alert('Sélectionnez au moins un ticket ACHAT pour le diffuser.');
      return;
    }
    if (onlyAchats.length < selectedTickets.length) {
      const skipped = selectedTickets.length - onlyAchats.length;
      if (!window.confirm(`${skipped} ticket(s) VENTE seront ignorés. Continuer avec ${onlyAchats.length} ACHAT(s) ?`)) return;
    }
    // Si au moins UN n'est pas diffusé → diffuser tout, sinon retirer
    const anyOff = onlyAchats.some(t => !t.diffused);
    const newDiffused = anyOff;
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/tickets/bulk-diffuse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_ids: onlyAchats.map(t => t.id), diffused: newDiffused }),
      });
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      const result = await response.json();
      alert(newDiffused
        ? `📡 ${result.updated} billet(s) maintenant diffusé(s) sur la page publique`
        : `${result.updated} billet(s) retiré(s) de la diffusion`);
      setSelectedIds(new Set());
      // Optimistic local update so the table reflects change without full reload
      if (typeof onLocalUpdate === 'function') {
        onLocalUpdate(onlyAchats.map(t => t.id), { diffused: newDiffused });
      }
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    }
  };


  // Bulk edit function
  const handleBulkEdit = async () => {
    if (selectedIds.size === 0) return;

    // SPECIAL CASE: quick placement (line-by-line block/rang/place)
    if (bulkEditField === 'placement_rapide') {
      if (!bulkEditValue.trim()) return;
      try {
        const response = await fetch('/api/tickets/quick-placement-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticket_ids: Array.from(selectedIds),
            text: bulkEditValue,
            project: project,
            mode: 'apply',
          }),
        });
        if (!response.ok) throw new Error(`Erreur serveur: ${response.status}`);
        const result = await response.json();
        setBulkEditOpen(false);
        setBulkEditField('');
        setBulkEditValue('');
        setQuickPlacementPreview(null);
        setSelectedIds(new Set());
        alert(`${result.matched} billet(s) modifié(s) (sur ${result.total_lines} ligne(s)) !`);
        window.location.reload();
      } catch (error) {
        alert(`❌ Erreur: ${error.message}`);
      }
      return;
    }

    if (!bulkEditField || bulkEditValue === '') return;
    
    try {
      // For "plan", we store the URL in preuve_envoi field
      const actualField = bulkEditField === 'plan' ? 'preuve_envoi' : bulkEditField;
      
      const response = await fetch(`/api/tickets/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_ids: Array.from(selectedIds),
          field: actualField,
          value: bulkEditValue,
          project: project,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Bulk update error response:', errorText);
        throw new Error(`Erreur serveur: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Close modal and reset
      setBulkEditOpen(false);
      const editedField = bulkEditField === 'plan' ? 'preuve_envoi' : bulkEditField;
      const editedValue = bulkEditValue;
      const editedIds = new Set(selectedIds);
      setBulkEditField('');
      setBulkEditValue('');
      
      // Notify parent so it can update its own state (préserve filtres + sélection)
      if (onBulkUpdated) {
        onBulkUpdated({ ids: Array.from(editedIds), field: editedField, value: editedValue });
      }
      
      toast.success(`${result.updated} billet(s) modifié(s) avec succès !`);
      // On NE recharge PAS la page : les filtres, le scroll et la sélection restent intacts.
      // La sélection est conservée pour permettre d'enchaîner d'autres modifications.
    } catch (error) {
      console.error('Bulk edit error:', error);
      toast.error(`Erreur lors de la modification en masse: ${error.message}`);
    }
  };

  const handleQuickPlacementPreview = async () => {
    if (!bulkEditValue.trim() || selectedIds.size === 0) return;
    setQuickPlacementLoading(true);
    try {
      const response = await fetch('/api/tickets/quick-placement-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_ids: Array.from(selectedIds),
          text: bulkEditValue,
          project: project,
          mode: 'preview',
        }),
      });
      const result = await response.json();
      setQuickPlacementPreview(result);
    } catch (error) {
      alert(`Erreur preview: ${error.message}`);
    } finally {
      setQuickPlacementLoading(false);
    }
  };

  const isPageSelected = paginatedTickets.length > 0 && paginatedTickets.every(t => selectedIds.has(t.id));

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Search and Bulk Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap w-full sm:w-auto">
          <div className="relative flex-1 min-w-[150px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="ticket-search-input"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background border-gold/20 focus:border-gold/50"
            />
          </div>
          {/* Clear filters button - show when filters are active */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              <X className="h-4 w-4 mr-1" />
              Réinitialiser filtres
            </Button>
          )}
          {/* Items per page selector */}
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-600 text-white text-sm rounded px-2 py-1.5 focus:border-gold/50 outline-none"
          >
            {PAGE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt} / page</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {filteredAndSortedTickets.length} lignes
            {stats && viewType === 'tout' && stats.total_tickets > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_tickets} total</span>
            )}
            {stats && viewType === 'achat' && stats.total_achats > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_achats} total</span>
            )}
            {stats && viewType === 'vente' && stats.total_ventes > filteredAndSortedTickets.length && (
              <span className="text-zinc-500"> / {stats.total_ventes} total</span>
            )}
          </span>
        </div>
        
        {/* Column Visibility & Reorder & Bulk Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Column Reorder Toggle */}
          {!reorderMode ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPendingOrder(activeOrder || columns.map(c => c.key));
                setReorderMode(true);
              }}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              data-testid="reorder-columns-btn"
            >
              <GripHorizontal className="h-4 w-4 mr-2" />
              Organiser les colonnes
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                onClick={saveColumnOrderToDb}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="save-columns-btn"
              >
                <Check className="h-4 w-4 mr-1" />
                Sauvegarder
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelReorder}
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                data-testid="cancel-reorder-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Annuler
              </Button>
              {columnOrder && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetColumnOrder}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  data-testid="reset-columns-btn"
                >
                  Reset
                </Button>
              )}
            </>
          )}
          {/* Column Visibility Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              >
                <Eye className="h-4 w-4 mr-2" />
                Colonnes
                {hiddenColumns.size > 0 && (
                  <span className="ml-1 text-[10px] bg-[#C4A052] text-black rounded-full px-1 font-bold">
                    {columns.length - hiddenColumns.size}/{columns.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
              <DropdownMenuItem 
                onSelect={(e) => {
                  e.preventDefault();
                  setHiddenColumns(new Set());
                }}
                className={cn(!hiddenColumns.size && "bg-zinc-700")}
              >
                <Eye className="h-4 w-4 mr-2" />
                <span>Afficher toutes</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {columns.filter(col => col.key !== 'select' && col.key !== 'actions').map((col) => {
                const isVisible = !hiddenColumns.has(col.key);
                return (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible}
                    onCheckedChange={() => toggleColumnVisibility(col.key)}
                    onSelect={(e) => e.preventDefault()}
                    className="text-xs"
                  >
                    {col.label || col.key}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Bulk Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllFiltered}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            Tout ({filteredAndSortedTickets.length})
          </Button>
          {selectedIds.size > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              >
                <Square className="h-4 w-4 mr-2" />
                Désélect.
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkEditOpen(true)}
                className="border-[#C4A052] text-[#C4A052] hover:bg-[#C4A052]/20"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modifier ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySelection}
                className="border-blue-500 text-blue-500 hover:bg-blue-500/20"
                data-testid="bulk-copy-button"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copier ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDesattribuer}
                className="border-orange-500 text-orange-500 hover:bg-orange-500/20"
                data-testid="bulk-desattribuer-button"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Désattribuer ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDiffuse}
                className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                data-testid="bulk-diffuse-button"
                title="Diffuser sur la page publique de vente"
              >
                <Radio className="h-4 w-4 mr-2" />
                Diffuser ({selectedIds.size})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ({selectedIds.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            ««
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            ‹ Préc
          </Button>
          <span className="text-sm text-white px-4">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            Suiv ›
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
          >
            »»
          </Button>
        </div>
      )}

      {/* PANNEAU RÉCAPITULATIF - Affiché quand un filtre est actif */}
      <FilterSummaryPanel 
        tickets={filteredAndSortedTickets} 
        hasFilter={hasActiveFilters || searchTerm.length > 0}
        searchTerm={searchTerm}
        project={project}
        viewType={viewType}
        onMatchClick={(matchNum) => {
          setRecapMatchFilter(matchNum);
          setCurrentPage(1);
        }}
        selectedMatchFilter={recapMatchFilter}
        onClearMatchFilter={() => {
          setRecapMatchFilter(null);
          setCurrentPage(1);
        }}
      />

      {/* Table */}
      <div className="table-container rounded-lg border border-gold/20 overflow-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 350px)' }}>
        <Table>
          <TableHeader className="sticky top-0 z-20" style={{ backgroundColor: 'var(--card, #1a1a1a)' }}>
            <TableRow className="hover:bg-transparent border-b border-gold/20">
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'select-none relative',
                    'text-xs uppercase tracking-wide font-bold text-white',
                    col.key === 'actions' || col.key === 'select' ? 'cursor-default' : ''
                  )}
                  style={{ 
                    width: columnWidths[col.key] || col.width, 
                    minWidth: '50px',
                    backgroundColor: '#0d0b09'
                  }}
                >
                  {col.key === 'select' ? (
                    <Checkbox
                      checked={isPageSelected}
                      onCheckedChange={toggleSelectAll}
                      className="border-zinc-500"
                    />
                  ) : col.key === 'actions' ? (
                    <span></span>
                  ) : col.key === 'attribuer' ? (
                    // Multi-select Status filter dropdown for Attribuer column
                    (() => {
                      const STATUS_LABELS = {
                        'non_attribue': { label: 'Non attribué', color: 'text-zinc-400' },
                        'attribue': { label: 'Attribué', color: 'text-blue-400' },
                        'valide': { label: 'Livré', color: 'text-emerald-400' },
                        'special': { label: 'Spécial', color: 'text-amber-400' },
                        'annule': { label: 'Annulé', color: 'text-red-400' },
                      };
                      const toggleStatus = (key) => {
                        setStatusFilter(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
                      };
                      const triggerLabel = statusFilter.length === 0
                        ? 'Statut'
                        : statusFilter.length === 1
                          ? STATUS_LABELS[statusFilter[0]]?.label || 'Statut'
                          : `Statut (${statusFilter.length})`;
                      return (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-1 hover:text-[#C4A052] transition-colors" data-testid="status-filter-trigger">
                              <span className={cn(statusFilter.length > 0 && "text-[#C4A052]")}>{triggerLabel}</span>
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="w-44" onCloseAutoFocus={(e) => e.preventDefault()}>
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); setStatusFilter([]); }}
                              className={cn(statusFilter.length === 0 && "bg-zinc-700")}
                              data-testid="status-filter-all"
                            >
                              <X className="h-3 w-3 mr-2 text-zinc-400" />
                              <span className="text-zinc-300">Tous (effacer)</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => (
                              <DropdownMenuCheckboxItem
                                key={key}
                                checked={statusFilter.includes(key)}
                                onCheckedChange={() => toggleStatus(key)}
                                onSelect={(e) => e.preventDefault()}
                                className="text-xs cursor-pointer"
                                data-testid={`status-filter-${key}`}
                              >
                                <span className={color}>{label}</span>
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()
                  ) : (
                    // Regular column with sort + filter
                    <div className="flex items-center gap-1">
                      {/* Reorder: Left arrow */}
                      {reorderMode && (
                        <button
                          onClick={(e) => { e.stopPropagation(); moveColumn(col.key, -1); }}
                          className="p-0.5 rounded hover:bg-[#C4A052]/30 text-zinc-400 hover:text-[#C4A052] transition-colors"
                          title="Déplacer à gauche"
                        >
                          <ArrowLeft className="h-3 w-3" />
                        </button>
                      )}
                      {/* Sort on click */}
                      <button 
                        onClick={() => !reorderMode && handleSort(col.key)}
                        className="flex items-center hover:text-[#C4A052] transition-colors"
                      >
                        <span className={cn(
                          columnFilters[col.key] && "text-[#C4A052]"
                        )}>
                          {col.label}
                        </span>
                        {!reorderMode && <SortIcon columnKey={col.key} sortConfig={sortConfig} />}
                      </button>
                      {/* Reorder: Right arrow */}
                      {reorderMode && (
                        <button
                          onClick={(e) => { e.stopPropagation(); moveColumn(col.key, 1); }}
                          className="p-0.5 rounded hover:bg-[#C4A052]/30 text-zinc-400 hover:text-[#C4A052] transition-colors"
                          title="Déplacer à droite"
                        >
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      )}
                      
                      {/* Filter dropdown - Uses backend values for complete list */}
                      {!reorderMode && (
                      <ColumnFilterDropdown
                        columnKey={col.key}
                        tickets={tickets}
                        columnFilters={columnFilters}
                        otherFiltersTickets={getTicketsFilteredExcept(col.key)}
                        allFilterValues={allFilterValues}
                        onFilterChange={(key, value) => {
                          setColumnFilters(prev => ({
                            ...prev,
                            [key]: value
                          }));
                        }}
                      />
                      )}
                    </div>
                  )}
                  {/* Resize handle */}
                  {col.key !== 'select' && col.key !== 'actions' && (
                    <div
                      onMouseDown={(e) => handleResizeStart(e, col.key, columnWidths[col.key] || col.width)}
                      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-[#C4A052]/50 transition-colors"
                      style={{ zIndex: 10 }}
                    />
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                    <span className="text-muted-foreground">Chargement...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredAndSortedTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="text-center py-12">
                  <p className="text-muted-foreground">Aucun billet trouvé</p>
                </TableCell>
              </TableRow>
            ) : (
              (() => {
                // Build ref_vente group lookup for visual grouping (RG only)
                const isRGGrouping = isRG && (viewType === 'tout' || viewType === 'vente' || viewType === 'achat');
                const refGroups = {};
                
                // For presorted mode: build groups using _groupStart/_groupSize tags from App.js
                const presortedGroups = {}; // idx -> { groupIdx, isFirst, isLast, color }
                if (presorted) {
                  const groupColorValues = ['#f59e0b', '#06b6d4', '#3b82f6', '#10b981', '#a855f7', '#f43f5e'];
                  let colorIdx = 0;
                  
                  paginatedTickets.forEach((t, idx) => {
                    if (t._groupStart && t._groupSize > 0) {
                      const color = groupColorValues[colorIdx % groupColorValues.length];
                      const size = Math.min(t._groupSize, paginatedTickets.length - idx);
                      for (let i = 0; i < size; i++) {
                        const targetIdx = idx + i;
                        if (targetIdx < paginatedTickets.length && !paginatedTickets[targetIdx]._orphan) {
                          presortedGroups[targetIdx] = {
                            color,
                            isFirst: i === 0,
                            isLast: i === size - 1,
                            isInGroup: true
                          };
                        }
                      }
                      colorIdx++;
                    }
                  });
                }
                
                if (isRGGrouping && !presorted) {
                  paginatedTickets.forEach((t, idx) => {
                    const ref = t.ref_vente?.trim() || '';
                    if (ref) {
                      if (!refGroups[ref]) refGroups[ref] = [];
                      refGroups[ref].push(idx);
                    }
                  });
                }
                // Colors for alternating groups (non-presorted mode)
                const groupColors = ['border-blue-500/40', 'border-emerald-500/40', 'border-amber-500/40', 'border-purple-500/40', 'border-cyan-500/40'];
                let groupColorIdx = 0;
                const refColorMap = {};
                Object.keys(refGroups).forEach(ref => {
                  if (refGroups[ref].length > 1) {
                    refColorMap[ref] = groupColors[groupColorIdx % groupColors.length];
                    groupColorIdx++;
                  }
                });

                return paginatedTickets.map((ticket, ticketIdx) => {
                // Presorted group info
                const pGroup = presortedGroups[ticketIdx];
                const isNewGroup = presorted && pGroup?.isFirst && ticketIdx > 0;
                // Detect start of orphan section
                const prevTicket = ticketIdx > 0 ? paginatedTickets[ticketIdx - 1] : null;
                const isFirstOrphan = presorted && ticket._orphan && (!prevTicket || !prevTicket._orphan);
                
                // Determine row status for CSS targeting
                const normalizedStatus = ticket.statut?.toUpperCase?.() || '';
                const ticketType = ticket.type?.toUpperCase?.() || '';
                let rowStatus = 'default';
                
                if (normalizedStatus === 'LIVRE') {
                    rowStatus = 'livre';
                } else if (normalizedStatus === 'VALIDE') {
                    rowStatus = 'valide';
                } else if (normalizedStatus === 'SPECIAL_MATCH') {
                    rowStatus = 'special';
                } else if (normalizedStatus === 'ANNULEE' || normalizedStatus === 'ANNULÉ') {
                    rowStatus = 'annulee';
                } else if (ticket.attribution_status === 'partial') {
                    rowStatus = 'partial';
                } else if (ticketType === 'VENTE' && ticket.attribue) {
                    rowStatus = 'attribue';
                } else if (ticketType === 'ACHAT' && ticket.ref_vente?.trim()) {
                    rowStatus = 'attribue';
                }
                
                const ref = ticket.ref_vente?.trim() || '';
                const groupColor = ref && refColorMap[ref];
                const groupIndices = ref && refGroups[ref];
                const isFirstInGroup = groupColor && groupIndices && groupIndices[0] === ticketIdx;
                const isLastInGroup = groupColor && groupIndices && groupIndices[groupIndices.length - 1] === ticketIdx;
                const isInGroup = !!groupColor;

                return (
                <React.Fragment key={ticket.id}>
                {/* Group separator for presorted view */}
                {isNewGroup && (
                  <TableRow className="h-4 bg-transparent border-0">
                    <TableCell colSpan={visibleColumns.length} className="p-0 border-0" />
                  </TableRow>
                )}
                {/* Orphan section separator */}
                {isFirstOrphan && (
                  <TableRow className="bg-zinc-800/80 border-0">
                    <TableCell colSpan={visibleColumns.length} className="py-2 px-4 text-center text-xs text-zinc-400 font-semibold tracking-wider border-t-2 border-dashed border-zinc-600">
                      BILLETS NON LIÉS ({paginatedTickets.filter(t => t._orphan).length})
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  data-testid={`ticket-row-${ticket.id}`}
                  data-row-status={rowStatus}
                  className={cn(
                    'transition-colors hover:bg-gold/5',
                    // Apply status background but strip border classes in presorted mode
                    !isLightMode && (pGroup?.isInGroup
                      ? getRowStatusClass(ticket.statut, ticket)?.replace(/border-l-4|!border-l-\S+/g, '')
                      : getRowStatusClass(ticket.statut, ticket)),
                    selectedIds.has(ticket.id) && 'bg-gold/15 ring-2 ring-[#C4A052] ring-inset',
                    !presorted && isRG && ticket.type === 'VENTE' && expandedRows.has(ticket.id) && 'border-l-4 border-r-4 border-t-4 border-cyan-500/50 !border-b-0',
                    // Non-presorted group borders
                    !presorted && isInGroup && `border-l-4 border-r-4 ${groupColor}`,
                    !presorted && isFirstInGroup && `border-t-4 ${groupColor}`,
                    !presorted && isLastInGroup && `border-b-4 ${groupColor}`,
                    !presorted && isInGroup && !isLastInGroup && '!border-b-0',
                    // 🟣 LITIGE highlight (purple) — applied last to override other backgrounds
                    ticket.litige && '!bg-purple-950/40 hover:!bg-purple-900/40 ring-1 ring-purple-500/50 ring-inset',
                    // 🟡 ACTION REQUISE highlight (yellow) — applied last after litige
                    ticket.action_requise && !ticket.litige && '!bg-yellow-900/30 hover:!bg-yellow-800/30 ring-1 ring-yellow-500/50 ring-inset',
                    // Si les deux flags actifs : violet prend le dessus mais on ajoute un ring jaune
                    ticket.action_requise && ticket.litige && 'ring-1 ring-yellow-400/70 ring-offset-1 ring-offset-purple-950'
                  )}
                  style={pGroup?.isInGroup ? {
                    boxShadow: [
                      `inset 5px 0 0 0 ${pGroup.color}`,    // left
                      `inset -5px 0 0 0 ${pGroup.color}`,   // right
                      pGroup.isFirst ? `inset 0 5px 0 0 ${pGroup.color}` : '',  // top
                      pGroup.isLast ? `inset 0 -5px 0 0 ${pGroup.color}` : '',  // bottom
                    ].filter(Boolean).join(', '),
                  } : {}}
                >
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key} className="py-1 px-2">
                      {col.key === 'select' ? (
                        <Checkbox
                          checked={selectedIds.has(ticket.id)}
                          onCheckedChange={() => toggleSelect(ticket.id)}
                          className="border-zinc-500"
                        />
                      ) : col.key === 'actions' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-gold/10"
                              data-testid={`ticket-actions-${ticket.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            {ticket.statut === 'MATCH_OK' && (
                              <DropdownMenuItem
                                data-testid={`unmatch-ticket-${ticket.id}`}
                                onClick={() => onUnmatch?.(ticket.id)}
                              >
                                <Link2Off className="h-4 w-4 mr-2" />
                                Unmatch
                              </DropdownMenuItem>
                            )}
                            {/* Désattribuer - for any attributed/validated/special ticket */}
                            {(ticket.attribue || ticket.statut === 'VALIDE' || ticket.statut === 'MATCH_OK' || ticket.statut === 'SPECIAL_MATCH' || ticket.statut === 'LIVRE' || (ticket.ref_vente && ticket.ref_vente.trim())) && (
                              <DropdownMenuItem
                                onClick={() => onMarkAttribue?.(ticket.id, false)}
                                className="text-amber-400"
                              >
                                <Link2Off className="h-4 w-4 mr-2" />
                                Désattribuer
                              </DropdownMenuItem>
                            )}
                            {!ticket.statut && (
                              <DropdownMenuItem
                                onClick={() => onUpdateStatus?.(ticket.id, 'VALIDE')}
                              >
                                <Check className="h-4 w-4 mr-2 text-emerald-400" />
                                Livrer
                              </DropdownMenuItem>
                            )}
                            {(ticket.statut === 'LIVRE' || ticket.statut === 'VALIDE') && (
                              <DropdownMenuItem
                                onClick={() => onUnvalidate?.(ticket.id)}
                                className="text-blue-400"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Repasser en Attribué
                              </DropdownMenuItem>
                            )}
                            {ticket.statut !== 'ANNULEE' && (
                              <DropdownMenuItem
                                onClick={() => onUpdateStatus?.(ticket.id, 'ANNULEE')}
                              >
                                <X className="h-4 w-4 mr-2 text-red-400" />
                                Annuler
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onEdit?.(ticket)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`delete-ticket-${ticket.id}`}
                              onClick={() => onDelete?.(ticket.id)}
                              className="text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        getCellValue(ticket, col.key)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Expandable client rows for RG VENTE tickets only */}
                {isRG && ticket.type === 'VENTE' && expandedRows.has(ticket.id) && (() => {
                  const qty = ticket.qty || 1;
                  const hasClients = ticket.clients?.length > 0;
                  const isEditing = !!editingClientsMap[ticket.id];
                  
                  return (
                  <TableRow className="border-0">
                    <TableCell colSpan={visibleColumns.length} className="py-3 px-4 bg-cyan-950/10 border-l-4 border-r-4 border-b-4 border-cyan-500/50 rounded-b-lg">
                      {isEditing ? (
                        /* EDIT MODE - grid 4 per row, bigger cards */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {(editingClientsMap[ticket.id] || []).map((client, idx) => (
                              <div key={idx} className="bg-zinc-800/80 rounded-lg p-3 border border-cyan-500/30 space-y-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="bg-cyan-500/20 text-cyan-400 font-mono text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">{idx + 1}</span>
                                  <span className="text-cyan-400 text-xs font-medium">Billet {idx + 1}</span>
                                </div>
                                <input
                                  type="text"
                                  placeholder="Nom Prénom *"
                                  value={client.nom || ''}
                                  onChange={(e) => {
                                    setEditingClientsMap(prev => {
                                      const updated = [...(prev[ticket.id] || [])];
                                      updated[idx] = { ...updated[idx], nom: e.target.value };
                                      return { ...prev, [ticket.id]: updated };
                                    });
                                  }}
                                  className={`w-full bg-zinc-900 border rounded-md px-3 py-1.5 text-sm text-white focus:border-cyan-500 outline-none placeholder:text-zinc-500 ${!client.nom?.trim() ? 'border-red-500/60' : 'border-zinc-600'}`}
                                />
                                <input
                                  type="text"
                                  placeholder="DDN (JJ/MM/AAAA) *"
                                  value={client.dob || ''}
                                  onChange={(e) => {
                                    setEditingClientsMap(prev => {
                                      const updated = [...(prev[ticket.id] || [])];
                                      updated[idx] = { ...updated[idx], dob: e.target.value };
                                      return { ...prev, [ticket.id]: updated };
                                    });
                                  }}
                                  className={`w-full bg-zinc-900 border rounded-md px-3 py-1.5 text-sm text-white focus:border-cyan-500 outline-none placeholder:text-zinc-500 ${!client.dob?.trim() ? 'border-red-500/60' : 'border-zinc-600'}`}
                                />
                                <input
                                  type="text"
                                  placeholder="Email *"
                                  value={client.email || ''}
                                  onChange={(e) => {
                                    setEditingClientsMap(prev => {
                                      const updated = [...(prev[ticket.id] || [])];
                                      updated[idx] = { ...updated[idx], email: e.target.value };
                                      return { ...prev, [ticket.id]: updated };
                                    });
                                  }}
                                  className={`w-full bg-zinc-900 border rounded-md px-3 py-1.5 text-sm text-white focus:border-cyan-500 outline-none placeholder:text-zinc-500 ${!client.email?.trim() ? 'border-red-500/60' : 'border-zinc-600'}`}
                                />
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            {(() => {
                              const clients = editingClientsMap[ticket.id] || [];
                              const allComplete = clients.length > 0 && clients.every(c => c.nom && c.nom.trim() && c.email && c.email.includes('@') && c.dob && c.dob.trim());
                              return (
                                <>
                                  <Button
                                    size="sm"
                                    disabled={!allComplete}
                                    onClick={async () => {
                                      if (onUpdateClients) {
                                        await onUpdateClients(ticket.id, editingClientsMap[ticket.id]);
                                      }
                                      setEditingClientsMap(prev => {
                                        const copy = { ...prev };
                                        delete copy[ticket.id];
                                        return copy;
                                      });
                                    }}
                                    className={`h-7 px-4 text-sm text-white ${allComplete ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-zinc-700 cursor-not-allowed opacity-50'}`}
                                  >
                                    <Check className="h-3.5 w-3.5 mr-1" /> Sauvegarder
                                  </Button>
                                  {!allComplete && (
                                    <span className="text-red-400 text-xs self-center">Remplir nom + email + DDN pour chaque billet</span>
                                  )}
                                </>
                              );
                            })()}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingClientsMap(prev => {
                                  const copy = { ...prev };
                                  delete copy[ticket.id];
                                  return copy;
                                });
                                if (!hasClients) {
                                  setExpandedRows(prev => {
                                    const next = new Set(prev);
                                    next.delete(ticket.id);
                                    return next;
                                  });
                                }
                              }}
                              className="h-7 px-4 text-sm border-zinc-600 text-zinc-400"
                            >
                              Annuler
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* VIEW MODE - grid 4 per row, bigger cards */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {ticket.clients.map((client, idx) => (
                              <div key={idx} className="bg-zinc-800/60 rounded-lg p-3 border border-cyan-500/20 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="bg-cyan-500/20 text-cyan-400 font-mono text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">{idx + 1}</span>
                                  <span className="text-white text-sm font-medium">{client.nom || '-'}</span>
                                </div>
                                {client.dob && <p className="text-zinc-400 text-xs pl-8">{client.dob}</p>}
                                {client.email && <p className="text-zinc-400 text-xs pl-8">{client.email}</p>}
                              </div>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              const existing = ticket.clients || [];
                              const slots = Array.from({ length: qty }, (_, i) => existing[i] || { nom: '', dob: '', email: '' });
                              setEditingClientsMap(prev => ({ ...prev, [ticket.id]: slots }));
                            }}
                            className="h-7 px-3 text-sm border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (onUpdateClients) {
                                await onUpdateClients(ticket.id, []);
                              }
                            }}
                            className="h-7 px-3 text-sm border-red-500/40 text-red-400 hover:bg-red-500/10"
                            data-testid={`clear-clients-${ticket.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Vider
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })()}
                </React.Fragment>
              );})
              })()
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination en bas */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 py-3 border-t border-gold/10">
          <div className="flex items-center gap-2">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-zinc-800 border border-zinc-600 text-white text-sm rounded px-2 py-1.5 focus:border-gold/50 outline-none"
            >
              {PAGE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt} / page</option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">
              {startIndex + 1}-{Math.min(endIndex, filteredAndSortedTickets.length)} sur {filteredAndSortedTickets.length}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ««
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ‹
            </Button>
            <span className="px-3 text-sm text-white">
              Page {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 px-2"
            >
              »»
            </Button>
          </div>
        </div>
      )}
      
      {/* Stadium Plan Modal */}
      <StadiumPlanModal
        open={stadiumPlanOpen}
        onOpenChange={setStadiumPlanOpen}
        stadium={selectedStadium}
        matchInfo={selectedMatchInfo}
      />
      
      {/* Bulk Edit Modal */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-[#C4A052]/30">
          <DialogHeader>
            <DialogTitle className="text-[#C4A052] flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Modifier la sélection
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modifier {selectedIds.size} billet(s) sélectionné(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Field selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                Quel champ souhaitez-vous modifier ?
              </label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Sélectionner un champ..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {BULK_EDIT_FIELDS.map((field) => (
                    <SelectItem 
                      key={field.key} 
                      value={field.key}
                      className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                    >
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Value input */}
            {bulkEditField && bulkEditField !== 'preuve_envoi' && bulkEditField !== 'placement_rapide' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-300">
                  Nouvelle valeur pour "{BULK_EDIT_FIELDS.find(f => f.key === bulkEditField)?.label}"
                </label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Entrez la nouvelle valeur..."
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>
            )}

            {/* Quick placement (line-by-line) */}
            {bulkEditField === 'placement_rapide' && (
              <div className="space-y-2">
                <div className="bg-zinc-800 border border-[#C4A052]/30 rounded p-3 text-sm space-y-1">
                  <p className="text-[#C4A052] font-semibold">Collez une ligne par billet sélectionné ({selectedIds.size} billet(s))</p>
                  <p className="text-zinc-400 text-xs">Format reconnu : <span className="text-zinc-200">"Section 247 - Rang V - Siège 5"</span>, <span className="text-zinc-200">"Block 247 - Row V - Seat 5"</span>, ou 3 valeurs séparées par <span className="text-zinc-200">-</span> / <span className="text-zinc-200">/</span> / <span className="text-zinc-200">|</span></p>
                  <p className="text-zinc-400 text-xs">Ordre : <strong>ligne 1 → 1er billet</strong>, ligne 2 → 2e billet, etc. (selon ordre de sélection)</p>
                </div>
                <textarea
                  value={bulkEditValue}
                  onChange={(e) => { setBulkEditValue(e.target.value); setQuickPlacementPreview(null); }}
                  placeholder={"Upper Tier - Section 247 - Rang V - Siège 5\nUpper Tier - Section 247 - Rang V - Siège 6\nUpper Tier - Section 247 - Rang V - Siège 7"}
                  rows={8}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded text-white placeholder:text-zinc-500 px-3 py-2 font-mono text-sm"
                  data-testid="quick-placement-textarea"
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!bulkEditValue.trim() || quickPlacementLoading}
                    onClick={handleQuickPlacementPreview}
                    className="border-[#C4A052] text-[#C4A052]"
                    data-testid="quick-placement-preview-btn"
                  >
                    {quickPlacementLoading ? 'Analyse…' : 'Aperçu'}
                  </Button>
                </div>
                {quickPlacementPreview && (
                  <div className="bg-zinc-950 border border-zinc-700 rounded p-2 max-h-64 overflow-auto">
                    <p className="text-xs text-zinc-400 mb-1">
                      {quickPlacementPreview.matched} à modifier · {quickPlacementPreview.no_data || 0} déjà identique / sans données · {quickPlacementPreview.ambiguous || 0} en trop
                    </p>
                    <table className="w-full text-xs">
                      <thead className="text-zinc-500">
                        <tr><th className="text-left">#</th><th className="text-left">Statut</th><th className="text-left">Match</th><th className="text-left">Cat</th><th className="text-left">Ref</th><th className="text-left">Actuel</th><th className="text-left">→ Nouveau</th></tr>
                      </thead>
                      <tbody>
                        {(quickPlacementPreview.results || []).map((r, i) => (
                          <tr key={i} className={`border-t border-zinc-800 ${r.status === 'matched' ? 'bg-emerald-900/10' : r.status === 'ambiguous' ? 'bg-amber-900/20' : 'bg-zinc-900/30'}`}>
                            <td className="text-zinc-500">{r.line_no}</td>
                            <td className={r.status === 'matched' ? 'text-emerald-400' : r.status === 'ambiguous' ? 'text-amber-400' : 'text-zinc-500'}>{r.status}</td>
                            <td className="text-zinc-300 truncate max-w-[150px]" title={r.evenement}>{(r.evenement || '').slice(0, 24)}</td>
                            <td className="text-zinc-400">{r.categorie || ''}</td>
                            <td className="text-amber-300 font-mono">{r.ref_vente || '—'}</td>
                            <td className="text-zinc-500">{r.current_block || '—'}/{r.current_rang || '—'}/{r.current_place || '—'}</td>
                            <td className="text-[#C4A052] font-bold">{r.new_block || '—'}/{r.new_rang || '—'}/{r.new_place || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            
            {/* Upload image for plan du stade */}
            {bulkEditField === 'plan' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-300">Plan du stade</label>
                <p className="text-xs text-zinc-500">Le plan sera mis à jour pour le stade des tickets sélectionnés</p>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    id="bulk-plan-upload"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        // Find stadium from selected tickets
                        const selectedTickets = tickets.filter(t => selectedIds.has(t.id));
                        const lieu = selectedTickets[0]?.lieu || '';
                        const ville = selectedTickets[0]?.ville || '';
                        const stadiumId = `${lieu}_${ville}`.replace(/\s+/g, '_').toLowerCase() || `custom_${Date.now()}`;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await fetch(`/api/stadiums/upload-plan?stadium_id=${encodeURIComponent(stadiumId)}`, {
                          method: 'POST',
                          body: formData
                        });
                        const data = await res.json();
                        if (data.success) {
                          setBulkEditValue(data.map_url);
                          toast.success('Image uploadée');
                        }
                      } catch (err) {
                        toast.error('Erreur upload');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('bulk-plan-upload')?.click()}
                    size="sm"
                    className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                  >
                    Insérer une image
                  </Button>
                  <Input
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="ou collez une URL d'image..."
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 flex-1"
                  />
                </div>
                {bulkEditValue && (
                  <img src={bulkEditValue} alt="Aperçu" className="max-h-[100px] rounded border border-zinc-600 mt-1" onError={(e) => { e.target.style.display = 'none'; }} onLoad={(e) => { e.target.style.display = 'block'; }} />
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBulkEditOpen(false);
                setBulkEditField('');
                setBulkEditValue('');
              }}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
            >
              Annuler
            </Button>
            <Button
              onClick={handleBulkEdit}
              disabled={!bulkEditField || bulkEditValue === ''}
              className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold disabled:opacity-50"
            >
              <Check className="h-4 w-4 mr-2" />
              Appliquer à {selectedIds.size} billet(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketTable;
